import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Eye,
  X,
  Edit2,
  Edit,
  Trash2,
  Save,
  Check,
  Send,
  X as XIcon,
  CheckSquare,
  Image as ImageIcon,
  Upload,
  Redo2,
  CornerDownRight,
  CornerUpLeft,
  Plus,
  Archive,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Calendar,
  AlignLeft,
  MessageSquare,
  Activity,
  History,
  Tag,
  Clock4,
  Paperclip,
  UserPlus,
  AlertTriangle,
  ChevronDown,
  Pencil,
  Bookmark,
  Link2,
  Search,
  Loader2,
  Sparkles,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import { useSocket } from "../contexts/SocketContext";
import { useBookmark } from "../contexts/BookmarkContext";
import { cardAPI, cardItemAPI, projectAPI, aiAPI } from "../utils/api";
import Avatar from "./Avatar";
import ConfirmationModal from "./ConfirmationModal";
import SimpleQuillEditor from "./SimpleQuillEditor";
import SimpleCommentEditor from "./SimpleCommentEditor";
import CustomDatePicker from "./CustomDatePicker";
import CustomStatusDropdown from "./CustomStatusDropdown";
import CustomProjectDropdown from "./CustomProjectDropdown";
import SafeImage from "./SafeImage";
import ImagePreviewModal from "./ImagePreviewModal";
import { TimeTrackerWidget, formatDuration } from "./TimeTracking";
import { API_URL } from "../utils/endpoints";
import { getCardStatusColors } from "../utils/statusColors";
import { features } from "../config/features";

const labelColors = [
  { name: "Light Green", value: "light-green", bg: "bg-green-300", text: "text-black" },
  { name: "Green", value: "green", bg: "bg-green-500", text: "text-white" },
  { name: "Dark Green", value: "dark-green", bg: "bg-green-700", text: "text-white" },
  { name: "Light Yellow", value: "light-yellow", bg: "bg-yellow-300", text: "text-black" },
  { name: "Yellow", value: "yellow", bg: "bg-yellow-500", text: "text-black" },
  { name: "Dark Yellow", value: "dark-yellow", bg: "bg-yellow-700", text: "text-white" },
  { name: "Orange", value: "orange", bg: "bg-orange-500", text: "text-white" },
  { name: "Red", value: "red", bg: "bg-red-500", text: "text-white" },
  { name: "Purple", value: "purple", bg: "bg-purple-500", text: "text-white" },
  { name: "Pink", value: "pink", bg: "bg-pink-500", text: "text-white" },
  { name: "Blue", value: "blue", bg: "bg-blue-500", text: "text-white" },
  { name: "Gray", value: "gray", bg: "bg-gray-500", text: "text-white" },
];

const getLabelColor = (color) => {
  if (color === "light-green") return labelColors.find((c) => c.value === "green");
  if (color === "light-yellow") return labelColors.find((c) => c.value === "yellow");
  return labelColors.find((c) => c.value === (color || "blue")) || labelColors[10];
};

const CardLabelsDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  availableLabels,
  selectedLabels,
  searchTerm,
  setSearchTerm,
  showCreateLabel,
  setShowCreateLabel,
  newLabelName,
  setNewLabelName,
  newLabelColor,
  setNewLabelColor,
  onToggleLabel,
  onCreateLabel,
  onUpdateLabel,
}) => {
  const dropdownRef = useRef(null);
  const [colorblindFriendly, setColorblindFriendly] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editLabelName, setEditLabelName] = useState("");
  const [editLabelColor, setEditLabelColor] = useState("blue");
  const [savingLabelId, setSavingLabelId] = useState(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const updatePosition = () => {
        const rect = anchorRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 280 && rect.top > 280;
        const width = rect.width;
        setCoords({
          top: openUp ? rect.top - 4 : rect.bottom + 4,
          left: rect.left,
          width,
          openUp,
        });
      };
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, anchorRef, showCreateLabel]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  useEffect(() => {
    if (!isOpen) {
      setShowCreateLabel(false);
      setEditingLabelId(null);
      setEditLabelName("");
      setEditLabelColor("blue");
      setSavingLabelId(null);
    }
  }, [isOpen, setShowCreateLabel]);

  if (!isOpen || !coords) return null;

  const normalizedSearch = (searchTerm || "").trim().toLowerCase();
  const filteredLabels = (availableLabels || []).filter((label) =>
    (label.name || "").toLowerCase().includes(normalizedSearch)
  );

  const handleCreateClick = async () => {
    await onCreateLabel();
  };

  const startEditingLabel = (label) => {
    const labelId = label._id || label.id || label.name;
    setEditingLabelId(labelId);
    setEditLabelName(label.name || "");
    setEditLabelColor(label.color || "blue");
  };

  const cancelEditingLabel = () => {
    setEditingLabelId(null);
    setEditLabelName("");
    setEditLabelColor("blue");
    setSavingLabelId(null);
  };

  const saveEditingLabel = async (label) => {
    const nextName = editLabelName.trim();
    if (!nextName) return;

    const labelId = label._id || label.id || label.name;
    setSavingLabelId(labelId);
    const updated = await onUpdateLabel(label, {
      name: nextName,
      color: editLabelColor,
    });
    setSavingLabelId(null);

    if (updated) {
      cancelEditingLabel();
    }
  };

  const renderLabelRow = (label) => {
    const colorConfig = getLabelColor(label.color);
    const isSelected = selectedLabels.some((item) => item.name === label.name);
    const labelKey = label._id || label.id || label.name;
    const colorName = colorConfig?.name || label.color || "Label";

    const isEditing = editingLabelId === labelKey;
    const isSaving = savingLabelId === labelKey;

    if (isEditing) {
      return (
        <div key={labelKey} className="p-2 bg-gray-50 dark:bg-slate-700/50 rounded-md my-1 space-y-2 border border-gray-200 dark:border-slate-600">
          <input
            type="text"
            value={editLabelName}
            onChange={(e) => setEditLabelName(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEditingLabel(label);
              if (e.key === "Escape") cancelEditingLabel();
            }}
            className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            autoFocus
            aria-label="Label name"
          />
          <select
            value={editLabelColor}
            onChange={(e) => setEditLabelColor(e.target.value)}
            className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            aria-label="Label color"
          >
            {labelColors.map((color) => (
              <option key={color.value} value={color.value}>
                {color.name}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => saveEditingLabel(label)}
              disabled={isSaving || !editLabelName.trim()}
              className="flex-1 rounded bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={cancelEditingLabel}
              disabled={isSaving}
              className="flex-1 rounded border border-gray-300 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div key={labelKey} className="flex items-center gap-2 py-0.5">
        <label className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {
              onToggleLabel(label);
              setSearchTerm?.("");
            }}
            className="h-4 w-4 rounded border-gray-400 bg-transparent text-blue-600 focus:ring-2 focus:ring-blue-500 dark:border-slate-500 dark:bg-slate-900"
            aria-label={`${isSelected ? "Remove" : "Apply"} ${label.name}`}
          />
        </label>
        <button
          type="button"
          onClick={() => {
            onToggleLabel(label);
            setSearchTerm?.("");
          }}
          className={`flex min-h-[32px] flex-1 items-center rounded px-3 text-left text-xs font-semibold transition-opacity hover:opacity-90 ${
            colorConfig?.bg || "bg-blue-500"
          } ${colorConfig?.text || "text-white"}`}
        >
          <span className="truncate">
            {colorblindFriendly && <span className="mr-2 opacity-80 font-normal">[{colorName}]</span>}
            {label.name}
          </span>
        </button>
        <button
          type="button"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white"
          title="Edit label"
          aria-label={`Edit ${label.name}`}
          onClick={() => startEditingLabel(label)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: coords.openUp ? undefined : `${coords.top}px`,
        bottom: coords.openUp ? `${window.innerHeight - coords.top}px` : undefined,
        left: `${coords.left}px`,
        width: `${coords.width}px`,
        zIndex: 99999,
      }}
      className="portal-dropdown bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-2xl overflow-hidden animate-slideDownFade"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="relative flex items-center justify-center px-8 py-2.5 border-b border-gray-100 dark:border-slate-700">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-slate-300">Labels</h3>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white transition-colors"
          title="Close labels"
          aria-label="Close labels"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3">
        {/* Labels List */}
        <div className="mb-2">
          <h4 className="px-0.5 pb-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400">Labels</h4>
          <div className={`${showCreateLabel ? "max-h-[76px]" : "max-h-[160px]"} overflow-y-auto space-y-1 pr-0.5 scrollbar-thin`}>
            {filteredLabels.length > 0 ? (
              filteredLabels.map(renderLabelRow)
            ) : (
              <p className="px-2 py-3 text-center text-xs text-gray-500 dark:text-slate-400">
                No labels found
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2 pt-1 border-t border-gray-100 dark:border-slate-700">
          {showCreateLabel ? (
            <div className="space-y-2 rounded border border-gray-200 dark:border-slate-700 p-2 bg-gray-50/50 dark:bg-slate-900/40">
              <input
                type="text"
                value={newLabelName}
                onChange={(e) => setNewLabelName(e.target.value.toUpperCase())}
                placeholder="Label name"
                className="w-full px-2.5 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              <select
                value={newLabelColor}
                onChange={(e) => setNewLabelColor(e.target.value)}
                className="w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-slate-100 outline-none focus:ring-1 focus:ring-blue-500"
              >
                {labelColors.map((color) => (
                  <option key={color.value} value={color.value}>
                    {color.name}
                  </option>
                ))}
              </select>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCreateClick}
                  disabled={!newLabelName.trim()}
                  className="flex-1 py-1.5 px-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateLabel(false)}
                  className="flex-1 py-1.5 px-2 text-xs font-semibold text-gray-600 dark:text-slate-300 border border-gray-300 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreateLabel(true)}
              className="w-full rounded border border-gray-300 dark:border-slate-600 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              Create a new label
            </button>
          )}

          <button
            type="button"
            onClick={() => setColorblindFriendly((prev) => !prev)}
            className={`w-full rounded border px-3 py-1.5 text-xs font-semibold transition-colors ${
              colorblindFriendly
                ? "border-blue-500 bg-blue-600 text-white hover:bg-blue-700"
                : "border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
            }`}
            aria-pressed={colorblindFriendly}
          >
            {colorblindFriendly ? "Disable colorblind friendly mode" : "Enable colorblind friendly mode"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CardMembersDropdown = ({
  isOpen,
  onClose,
  anchorRef,
  memberSearch,
  projectMembers,
  selectedAssignees,
  onToggleAssignee,
}) => {
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const updatePosition = () => {
        const rect = anchorRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < 280 && rect.top > 280;
        setCoords({
          top: openUp ? rect.top - 4 : rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          openUp,
        });
      };
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen, anchorRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        (!anchorRef?.current || !anchorRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen || !coords) return null;

  const normalizedSearch = (memberSearch || "").trim().toLowerCase();
  const matchesSearch = (member) => {
    if (!normalizedSearch) return true;
    return [member.name, member.email, member.role]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedSearch));
  };

  const assignedMembers = projectMembers
    .filter((member) => selectedAssignees.includes(member._id || member.id))
    .filter(matchesSearch);
  const boardMembers = projectMembers
    .filter((member) => !selectedAssignees.includes(member._id || member.id))
    .filter(matchesSearch);

  const renderMemberRow = (member, isAssigned) => (
    <button
      key={member._id || member.id}
      type="button"
      onClick={() => onToggleAssignee(member)}
      className={`group flex w-full items-center justify-between gap-2.5 px-2.5 py-1.5 text-left transition-colors duration-150 rounded ${
        isAssigned
          ? "bg-blue-50/80 text-blue-900 hover:bg-blue-100/80 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:bg-blue-900/50"
          : "text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700/60"
      }`}
      aria-label={`${isAssigned ? "Remove" : "Assign"} ${member.name || "member"}`}
    >
      <span className="flex min-w-0 items-center gap-2">
        <Avatar user={member} size="xs" />
        <span className="min-w-0">
          <span className="block truncate text-xs font-medium leading-4">{member.name || "Unknown User"}</span>
          {member.email && (
            <span className="block truncate text-[10px] leading-3 text-gray-500 dark:text-slate-400">{member.email}</span>
          )}
        </span>
      </span>
      {isAssigned && (
        <X className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-red-500 dark:text-slate-400 transition-colors" />
      )}
    </button>
  );

  return createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: "fixed",
        top: coords.openUp ? undefined : `${coords.top}px`,
        bottom: coords.openUp ? `${window.innerHeight - coords.top}px` : undefined,
        left: `${coords.left}px`,
        width: `${coords.width}px`,
        zIndex: 99999,
      }}
      className="portal-dropdown bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg shadow-2xl overflow-hidden animate-slideDownFade"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="relative flex items-center justify-center px-8 py-2.5 border-b border-gray-100 dark:border-slate-700">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-slate-300">Members</h3>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white transition-colors"
          title="Close members"
          aria-label="Close members"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-3">
        <div className="max-h-[220px] overflow-y-auto space-y-2 pr-0.5 scrollbar-thin">
          <section aria-labelledby="card-members-heading">
            <h4 id="card-members-heading" className="px-0.5 pb-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400">Card members</h4>
            <div className="space-y-0.5">
              {assignedMembers.length > 0 ? (
                assignedMembers.map((member) => renderMemberRow(member, true))
              ) : (
                <p className="px-2 py-1 text-xs text-gray-500 dark:text-slate-400 italic">No card members</p>
              )}
            </div>
          </section>

          <section aria-labelledby="board-members-heading" className="pt-2 border-t border-gray-100 dark:border-slate-700">
            <h4 id="board-members-heading" className="px-0.5 pb-1 text-[11px] font-semibold text-gray-500 dark:text-slate-400">Board members</h4>
            <div className="space-y-0.5">
              {boardMembers.length > 0 ? (
                boardMembers.map((member) => renderMemberRow(member, false))
              ) : (
                <p className="px-2 py-1 text-xs text-gray-500 dark:text-slate-400 italic">
                  {projectMembers.length === 0 ? "No project members found" : "No board members found"}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CardModal = ({
  card,
  cards = [],
  onClose,
  onCardUpdated,
  onCardDeleted,
  onCardRestored,
  onCardPermanentlyDeleted,
  onStatusChange,
  onNavigateCard,
  activeTimer,
  activeTimers,
  onTimerStart,
  onTimerStop,
}) => {
  const { users, user } = useUser();
  const { currentProject, projects } = useProject();
  const { showToast } = useNotification();
  const { isBookmarked, toggleBookmark } = useBookmark();
  const bookmarked = isBookmarked(card._id);
  const { socket } = useSocket();

  // Check if card is archived
  const isArchived = card.isArchived || card.status === "archive";

  // Helper function to check if a comment is a system-generated comment / work log
  const isLegacySystemComment = (comment) => {
    if (!comment) return false;
    if (comment.kind === "user") return false;
    if (comment.action) return true;
    const text = (comment.text || "").trim();
    return /^(?:<p><strong>[^<]+<\/strong>\s+|[^<\n]+\s+)(?:uploaded an image:?|uploaded an attachment:?|uploaded a file:?|removed an image:?|removed an attachment:?|removed a file:?|moved this card from\b|archived this card\b|restored this card\b|renamed the card\b|created this card\b|assigned .+ to this card\b|removed .+ from this card\b|changed priority\b|changed the due date\b|updated due date\b|updated the description\b|added a description\b|removed the description\b|marked this card as\b|bookmarked\b)/i.test(text);
  };



  // Mention functionality - now handled by QuillEditor
  const handleMentionSelect = (mention, newText) => {
    setMentions((prev) => [...prev, mention]);
  };

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  // AI description transformation state
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiConvertedDescription, setAiConvertedDescription] = useState(null); // { mode, content }
  const aiMenuRef = useRef(null);
  const [formData, setFormData] = useState({
    title: card.title,
    description: card.description,
    dueDate: card.dueDate
      ? new Date(card.dueDate).toLocaleDateString('en-CA')
      : "",
  });
  const [commentText, setCommentText] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  // Files picked to go out WITH the comment being composed (Trello-style) —
  // not sent until the comment is submitted, so the user can remove them
  // first, and nothing is uploaded if they never hit send.
  const [pendingCommentFiles, setPendingCommentFiles] = useState([]);
  const [isUploadingCommentFiles, setIsUploadingCommentFiles] = useState(false);
  const commentFileInputRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteCardConfirm, setShowDeleteCardConfirm] = useState(false);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const [userToUnassign, setUserToUnassign] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [showLabelsDropdown, setShowLabelsDropdown] = useState(false);
  const [showMembersDropdown, setShowMembersDropdown] = useState(false);
  const labelsSectionRef = useRef(null);
  const labelInputRef = useRef(null);
  const assigneeSectionRef = useRef(null);
  const memberInputRef = useRef(null);
  const [labelSearchTerm, setLabelSearchTerm] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState("blue");
  const [newChecklist, setNewChecklist] = useState("");
  const [items, setItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [loadingItems, setLoadingItems] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemTitle, setEditingItemTitle] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [deletingAttachment, setDeletingAttachment] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");
  // Files picked to be added to a comment while EDITING it (separate from
  // pendingCommentFiles, which is for composing a brand-new comment).
  const [editPendingFiles, setEditPendingFiles] = useState([]);
  const [isUploadingEditFiles, setIsUploadingEditFiles] = useState(false);
  const editFileInputRef = useRef(null);
  // Existing images/files already on the comment being edited — kept out of
  // the editable rich-text area (editing images comes later) and preserved
  // read-only, then re-attached automatically when the edit is saved.
  const [editExistingAttachmentHtml, setEditExistingAttachmentHtml] =
    useState("");
  // Reply to comment state (Jira-style threaded comments)
  const [replyingToCommentId, setReplyingToCommentId] = useState(null);
  const [replyToUser, setReplyToUser] = useState(null);
  const [replyCommentText, setReplyCommentText] = useState("");
  const [replyMentions, setReplyMentions] = useState([]);
  const [replyPendingFiles, setReplyPendingFiles] = useState([]);
  const [isUploadingReplyFiles, setIsUploadingReplyFiles] = useState(false);
  const replyFileInputRef = useRef(null);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [showDeleteCommentConfirm, setShowDeleteCommentConfirm] = useState(false);
  const [activeActivityTab, setActiveActivityTab] = useState("comments"); // "comments" | "work_log"
  const [mentions, setMentions] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [selectedProjectForMove, setSelectedProjectForMove] = useState(null);
  const [selectedProjectColumns, setSelectedProjectColumns] = useState([]);
  const [loadingProjectChange, setLoadingProjectChange] = useState(false);
  const [movedProjectName, setMovedProjectName] = useState(null);
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showRichText, setShowRichText] = useState(true); // Default to rich text
  const [activeFormatting, setActiveFormatting] = useState({
    bold: false,
    italic: false,
    underline: false,
    header: false,
    list: false,
    link: false,
  });
  const [autoSaving, setAutoSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const modalRef = useRef(null);
  const formattingHelpModalRef = useRef(null);
  const initialDueDateRef = useRef(formData.dueDate);
  const cardIdRef = useRef(card._id);
  const dateInputRef = useRef(null);
  const titleTextareaRef = useRef(null);
  const cardProjectId = card.project?._id || card.project || currentProject?._id;

  // Related Cards state
  const [showRelatedCardsSearch, setShowRelatedCardsSearch] = useState(false);
  const [relatedSearchQuery, setRelatedSearchQuery] = useState("");
  const [relatedSearchResults, setRelatedSearchResults] = useState([]);
  const [isSearchingRelated, setIsSearchingRelated] = useState(false);
  const [relatedLoadingCardId, setRelatedLoadingCardId] = useState(null);
  const relatedSearchInputRef = useRef(null);
  const relatedCardsContainerRef = useRef(null);

  // Search for related cards
  useEffect(() => {
    if (!showRelatedCardsSearch) {
      setRelatedSearchQuery("");
      setRelatedSearchResults([]);
      return;
    }

    if (!cardProjectId) return;

    const trimmed = relatedSearchQuery.trim();
    if (!trimmed) {
      setRelatedSearchResults([]);
      setIsSearchingRelated(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingRelated(true);
        const res = await cardAPI.searchProjectCards(cardProjectId, trimmed);
        if (res.data?.success) {
          const filtered = (res.data.cards || []).filter(
            (c) => String(c._id) !== String(card._id)
          );
          setRelatedSearchResults(filtered);
        }
      } catch (err) {
        console.error("Error searching cards for related:", err);
      } finally {
        setIsSearchingRelated(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [relatedSearchQuery, showRelatedCardsSearch, card._id, cardProjectId, cards]);

  // Close AI menu on outside click
  useEffect(() => {
    if (!aiMenuOpen) return;
    const handleClickOutside = (e) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target)) {
        setAiMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [aiMenuOpen]);

  // Click outside to close related cards search
  useEffect(() => {
    if (!showRelatedCardsSearch) return;

    const handleClickOutside = (e) => {
      if (
        relatedCardsContainerRef.current &&
        !relatedCardsContainerRef.current.contains(e.target)
      ) {
        setShowRelatedCardsSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showRelatedCardsSearch]);

  // Toggle related card link/unlink
  const handleToggleRelatedCard = async (targetCardId) => {
    if (isArchived) {
      showToast("Cannot modify related cards on archived cards", "error");
      return;
    }
    const currentRelated = card.relatedCards || [];
    const isAlreadyRelated = currentRelated.some(
      (rc) => String(rc._id || rc) === String(targetCardId)
    );

    try {
      setRelatedLoadingCardId(targetCardId);
      if (isAlreadyRelated) {
        const res = await cardAPI.removeRelatedCard(card._id, targetCardId);
        if (res.data?.success) {
          onCardUpdated(res.data.card);
          showToast("Related card unlinked", "success");
        }
      } else {
        const res = await cardAPI.addRelatedCard(card._id, targetCardId);
        if (res.data?.success) {
          onCardUpdated(res.data.card);
          showToast("Related card linked", "success");
          setRelatedSearchQuery("");
          setRelatedSearchResults([]);
        }
      }
    } catch (err) {
      console.error("Error toggling related card:", err);
      showToast(err.response?.data?.message || "Failed to update related card", "error");
    } finally {
      setRelatedLoadingCardId(null);
    }
  };

  // Quick remove related card from list
  const handleRemoveRelatedCard = async (targetCardId, e) => {
    if (e) e.stopPropagation();
    if (isArchived) {
      showToast("Cannot modify related cards on archived cards", "error");
      return;
    }
    try {
      setRelatedLoadingCardId(targetCardId);
      const res = await cardAPI.removeRelatedCard(card._id, targetCardId);
      if (res.data?.success) {
        onCardUpdated(res.data.card);
        showToast("Related card unlinked", "success");
      }
    } catch (err) {
      console.error("Error unlinking related card:", err);
      showToast(err.response?.data?.message || "Failed to unlink card", "error");
    } finally {
      setRelatedLoadingCardId(null);
    }
  };

  // Auto-adjust title textarea height as text grows or shrinks (Jira style)
  useEffect(() => {
    if (isEditingTitle && titleTextareaRef.current) {
      const el = titleTextareaRef.current;
      el.style.height = "0px";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [formData.title, isEditingTitle]);

  // Mark card as read when modal opens
  useEffect(() => {
    const markCardAsRead = async () => {
      if (!card || !card._id || !user) return;

      // Check if card is already read by current user
      const isRead = card.readBy?.some(
        (readEntry) =>
          (readEntry.user?._id || readEntry.user) === (user._id || user.id)
      );

      if (!isRead) {
        try {
          const { cardAPI } = await import("../utils/api");
          const response = await cardAPI.markAsRead(card._id);
          if (response.data.success && onCardUpdated) {
            // Update card with read status
            onCardUpdated(response.data.card);
          }
        } catch (error) {
          console.error("Error marking card as read:", error);
          // Don't show error toast, just log it
        }
      }
    };

    markCardAsRead();
  }, [card._id, user, onCardUpdated]); // Only run when card ID or user changes

  // Reset moved project state when a new card is opened
  useEffect(() => {
    setMovedProjectName(null);
    setSelectedProjectForMove(null);
    setSelectedProjectColumns([]);
  }, [card._id]);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on confirmation modal or its children
      const confirmationModal = document.querySelector('[class*="z-[100]"]');
      if (confirmationModal && confirmationModal.contains(event.target)) {
        return;
      }

      if (modalRef.current && !modalRef.current.contains(event.target)) {
        // Don't close modal if any confirmation modal is open
        if (
          !showDeleteConfirm &&
          !showDeleteCardConfirm &&
          !showUnassignConfirm &&
          !showImageModal &&
          !showFormattingHelp &&
          !showLabelsDropdown &&
          !showMembersDropdown
        ) {
          onClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    onClose,
    showDeleteConfirm,
    showDeleteCardConfirm,
    showUnassignConfirm,
    showImageModal,
    showFormattingHelp,
    showLabelsDropdown,
    showMembersDropdown,
  ]);

  // Handle click outside to close formatting help modal
  useEffect(() => {
    if (!showFormattingHelp) return;

    const handleClickOutside = (event) => {
      if (
        formattingHelpModalRef.current &&
        !formattingHelpModalRef.current.contains(event.target)
      ) {
        setShowFormattingHelp(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFormattingHelp]);


  // Update ref when card changes
  useEffect(() => {
    cardIdRef.current = card._id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card._id]);


  useEffect(() => {
    let cancelled = false;

    const loadProjectLabels = async () => {
      if (!cardProjectId) {
        setAvailableLabels([]);
        return;
      }

      try {
        const response = await projectAPI.getProjectLabels(cardProjectId);
        if (!cancelled) {
          setAvailableLabels(response.data?.labels || []);
        }
      } catch (error) {
        console.error("Error loading project labels:", error);
        if (!cancelled) {
          setAvailableLabels(card.labels || []);
        }
      }
    };

    loadProjectLabels();
    return () => {
      cancelled = true;
    };
  }, [cardProjectId, card.labels]);
  // Sync formData when card prop changes (for real-time updates from other users)
  useEffect(() => {
    if (!isEditingTitle && !isEditingDescription) {
      // Only update if user is not currently editing
      const newDueDate = card.dueDate
        ? new Date(card.dueDate).toLocaleDateString('en-CA')
        : "";

      // Update the initial due date ref BEFORE setting formData to prevent auto-save trigger
      initialDueDateRef.current = newDueDate;

      setFormData({
        title: card.title || "",
        description: card.description || "",
        dueDate: newDueDate,
      });
    }
  }, [card.title, card.description, card.dueDate, isEditingTitle, isEditingDescription]);

  // Auto-save due date when it changes
  useEffect(() => {
    const autoSaveDueDate = async () => {
      // Skip if dueDate hasn't changed from initial value
      if (formData.dueDate === initialDueDateRef.current) {
        return;
      }

      // Skip if user is currently editing (let them save manually)
      if (isEditingTitle || isEditingDescription) {
        return;
      }

      setAutoSaving(true);
      try {
        const updateData = {
          title: formData.title,
          description: formData.description,
          // Send null instead of empty string to properly clear the date
          dueDate: formData.dueDate || null,
        };

        const response = await cardAPI.updateCard(
          cardIdRef.current,
          updateData
        );

        if (response.data.success) {
          onCardUpdated(response.data.card);
          initialDueDateRef.current = formData.dueDate;
          showToast(
            formData.dueDate ? "Due date updated" : "Due date cleared",
            "success"
          );
        }
      } catch (error) {
        console.error("Error auto-saving due date:", error);
        showToast("Failed to update due date", "error");
      } finally {
        setAutoSaving(false);
      }
    };

    // Debounce auto-save for 1 second
    const timer = setTimeout(() => {
      autoSaveDueDate();
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dueDate, isEditingTitle, isEditingDescription]);

  // File upload handler
  const handleFileUpload = useCallback(
    async (files) => {
      if (!files || files.length === 0) return;

      // Check file count limit
      if (files.length > 5) {
        showToast("Maximum 5 files can be uploaded at once", "error");
        return;
      }

      // Check file sizes (max 25MB each)
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) {
          showToast(`File "${file.name}" size must be less than 25MB`, "error");
          return;
        }
      }

      try {
        setIsUploading(true);

        // Create FormData for file upload
        const formData = new FormData();

        // Append all files
        for (const file of files) {
          formData.append("images", file);
        }

        // Upload the files using the API
        const response = await cardAPI.uploadFiles(card._id, formData);

        if (response.data.success) {
          onCardUpdated(response.data.card);
          const fileCount = files.length;
          const imageCount = files.filter((f) =>
            f.type.startsWith("image/")
          ).length;
          const docCount = fileCount - imageCount;

          let message = `${fileCount} file(s) uploaded successfully!`;
          if (imageCount > 0 && docCount > 0) {
            message = `${imageCount} image(s) and ${docCount} document(s) uploaded successfully!`;
          } else if (imageCount > 0) {
            message = `${imageCount} image(s) uploaded successfully!`;
          } else if (docCount > 0) {
            message = `${docCount} document(s) uploaded successfully!`;
          }

          showToast(message, "success");
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        const errorMessage =
          error.response?.data?.message || "Failed to upload files";
        showToast(errorMessage, "error");
      } finally {
        setIsUploading(false);
      }
    },
    [card._id, onCardUpdated, showToast]
  );

  // --- Comment-composer attachments (Trello-style: attach, preview, THEN send with the comment) ---

  const handleCommentFileSelect = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return;

      // Same limits as the card-level uploader, checked client-side up front
      // so a bad selection never even reaches the network.
      if (pendingCommentFiles.length + files.length > 5) {
        showToast("You can attach up to 5 files to a comment", "error");
        return;
      }
      const tooBig = files.find((f) => f.size > 25 * 1024 * 1024);
      if (tooBig) {
        showToast(`File "${tooBig.name}" size must be less than 25MB`, "error");
        return;
      }

      setPendingCommentFiles((prev) => [...prev, ...files]);
    },
    [pendingCommentFiles.length, showToast]
  );

  const removePendingCommentFile = useCallback((index) => {
    setPendingCommentFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Pasting an image (e.g. a screenshot) while typing in a comment/reply/edit
  // composer attaches it to THAT comment instead of the card. Used as a
  // capture-phase handler so it runs before Quill and stops the event from
  // reaching the card-level document paste listener.
  const handleComposerPaste = (onFiles) => (e) => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (files.length === 0) return;

    e.preventDefault();
    e.stopPropagation();
    onFiles(files);
  };

  // Drag and Drop handlers
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return;

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only set isDragging to false if we're leaving the modal entirely
      if (!modal.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files);
      }
    };

    // Paste handler
    const handlePaste = (e) => {
      const items = e.clipboardData.items;
      const files = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        handleFileUpload(files);
      }
    };

    modal.addEventListener("dragenter", handleDragEnter);
    modal.addEventListener("dragleave", handleDragLeave);
    modal.addEventListener("dragover", handleDragOver);
    modal.addEventListener("drop", handleDrop);
    document.addEventListener("paste", handlePaste);

    return () => {
      modal.removeEventListener("dragenter", handleDragEnter);
      modal.removeEventListener("dragleave", handleDragLeave);
      modal.removeEventListener("dragover", handleDragOver);
      modal.removeEventListener("drop", handleDrop);
      document.removeEventListener("paste", handlePaste);
    };
  }, [handleFileUpload]);

  // Dynamic status options based on project columns (excluding Archive)
  // Use selectedProjectColumns if card was just moved to a new project, otherwise use current project's columns
  const columnsToUse = selectedProjectForMove ? selectedProjectColumns : columns;
  const statusOptions = columnsToUse
    .filter((column) => column.status !== "archive")
    .map((column) => ({
      value: column.status,
      label: column.name,
      color: column.color || "blue",
    }));

  // Deduplicate status options by value (in case multiple columns have same status)
  const statusOptionsDeduped = statusOptions.filter((option, index, arr) =>
    arr.findIndex(o => o.value === option.value) === index
  );

  // Add current card status if it's not in the columns (fallback)
  const allStatusOptions = [...statusOptionsDeduped];
  if (card.status && !statusOptionsDeduped.find((s) => s.value === card.status)) {
    allStatusOptions.push({
      value: card.status,
      label: card.statusLabel || card.status,
      color: "gray",
    });
  }

  // Helper to resolve human-readable column/status label and prevent showing internal IDs (e.g. custom_...)
  const getCardStatusLabel = (statusCode, cardObj) => {
    if (!statusCode) return "";

    if (cardObj?.statusLabel && !cardObj.statusLabel.startsWith("custom_")) {
      return cardObj.statusLabel;
    }

    const foundOption = allStatusOptions.find((s) => s.value === statusCode);
    if (foundOption?.label && !foundOption.label.startsWith("custom_")) {
      return foundOption.label;
    }

    const foundCol = (columns || []).find((c) => c.status === statusCode);
    if (foundCol?.name && !foundCol.name.startsWith("custom_")) {
      return foundCol.name;
    }

    if (cards && cards.length > 0) {
      const matchCard = cards.find(
        (c) =>
          c._id === cardObj?._id ||
          (c.status === statusCode && c.statusLabel && !c.statusLabel.startsWith("custom_"))
      );
      if (matchCard?.statusLabel && !matchCard.statusLabel.startsWith("custom_")) {
        return matchCard.statusLabel;
      }
    }

    // Do NOT display internal IDs (custom_... or 24-character hexadecimal ObjectIds)
    if (statusCode.startsWith("custom_") || /^[0-9a-fA-F]{24}$/.test(statusCode)) {
      return "";
    }

    return statusCode.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Dynamic project options - sorted alphabetically (include current project for display)
  const allProjectOptions = (projects || [])
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
    .map((project) => ({
      value: project._id,
      label: project.name,
    }));

  // Filter options to exclude current project for selection
  const selectableProjectOptions = allProjectOptions.filter(
    (project) => project.value !== (card.project?._id || card.project)
  );

  const currentProjectLabel =
    movedProjectName ||
    allProjectOptions.find((p) => p.value === (card.project?._id || card.project))
      ?.label ||
    (currentProject?._id === (card.project?._id || card.project)
      ? currentProject.name
      : null) ||
    card.project?.name;

  // Debug: Show dependent dropdown information
  // console.log("CardModal Dependent Dropdowns Debug:", {
  //   currentCardProject: card.project,
  //   currentCardStatus: card.status,
  //   isMovingCard: !!selectedProjectForMove,
  //   selectedProjectForMove,
  //   currentStatusOptions: statusOptions.map(s => ({ value: s.value, label: s.label })),
  //   currentProjectsAvablable: selectableProjectOptions.length,
  //   movedProjectName,
  // });

  const getAssignees = () => {
    if (!card.assignees || !Array.isArray(card.assignees)) {
      return [];
    }

    return card.assignees
      .map((assignee) => {
        // If assignee is already a populated user object
        if (typeof assignee === "object" && assignee.name) {
          return assignee;
        }
        // If assignee is a user ID, find the user
        return users.find(
          (user) => user._id === assignee || user.id === assignee
        );
      })
      .filter(Boolean);
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
      : { r: 59, g: 130, b: 246 }; // Default blue
  };

  // Comment HTML (both the legacy "Upload Files" auto-comment and the
  // comment-composer attach flow) embeds relative "/uploads/..." paths since
  // the backend doesn't know the frontend's API origin. There's no dev-server
  // proxy configured, so a relative src/href would resolve against the
  // React app's own origin and 404 — rewrite to an absolute API_URL first.
  const resolveAttachmentUrls = (html) =>
    html.replace(
      /((?:src|href))="(\/uploads\/[^"]*)"/g,
      (_match, attr, path) => `${attr}="${API_URL}${path}"`
    );

  // Inverse of resolveAttachmentUrls — used before SAVING an edited comment,
  // so what's actually stored stays a portable relative path (consistent
  // with every other comment: compose, legacy auto-upload, imports) rather
  // than permanently baking in today's API_URL.
  const unresolveAttachmentUrls = (html) => {
    if (!API_URL) return html;
    const escapedApiUrl = API_URL.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return html.replace(
      new RegExp(`((?:src|href))="${escapedApiUrl}(\\/uploads\\/[^"]*)"`, "g"),
      (_match, attr, path) => `${attr}="${path}"`
    );
  };

  // Render comment text with styled mentions
  const renderCommentWithMentions = (html) => {
    if (!html) return "";

    html = resolveAttachmentUrls(html);

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const walk = (node) => {
      // Mentions selected in the Quill editor are already semantic, styled
      // spans. Leave them intact instead of wrapping their text a second time.
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.classList?.contains("mention")
      ) {
        return;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;

        const fragment = document.createDocumentFragment();

        const parts = text.split(/(@\w+)/g);

        parts.forEach((part) => {
          if (!part.startsWith("@")) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }

          const username = part.substring(1);

          const member = currentProject?.members?.find(
            (m) =>
              m.user.name
                .toLowerCase()
                .replace(/\s+/g, "") === username.toLowerCase()
          );

          const span = document.createElement("span");

          if (member) {
            const color = member.user.color || "#3b82f6";
            const rgb = hexToRgb(color);

            span.style.backgroundColor = `rgba(${rgb.r},${rgb.g},${rgb.b},0.15)`;
            span.style.color = color;
            span.style.padding = "2.5px 8px";
            span.style.borderRadius = "6px";
            span.style.fontWeight = "600";
            span.style.fontSize = "13.5px";
            span.style.border = `1px solid ${color}20`;

            span.textContent = part;
          } else {
            span.style.color = "#ef4444";
            span.style.fontSize = "13.5px";
            span.textContent = part;
          }

          fragment.appendChild(span);
        });

        node.parentNode.replaceChild(fragment, node);
      } else {
        Array.from(node.childNodes).forEach(walk);
      }
    };

    walk(doc.body);

    return doc.body.innerHTML;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Only send the fields that can be updated
      const updateData = {
        title: formData.title,
        description: formData.description,
        dueDate: formData.dueDate,
      };

      const response = await cardAPI.updateCard(card._id, updateData);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        setIsEditingTitle(false);
        setIsEditingDescription(false);
        showToast("Card updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error updating card:", error);
      showToast("Failed to update card", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await cardAPI.updateStatus(card._id, newStatus);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        onStatusChange(card._id, newStatus);

        const statusLabel =
          allStatusOptions.find((s) => s.value === newStatus)?.label ||
          newStatus;
        showToast(`Card moved to ${statusLabel}`, "success");

        // Clear selectedProjectForMove so dropdown shows the selected status
        if (selectedProjectForMove) {
          setSelectedProjectForMove(null);
          setSelectedProjectColumns([]);
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showToast("Failed to update status", "error");
    }
  };

  const handleProjectChange = async (newProjectId) => {
    // If project hasn't changed, just return
    const currentProjectId = card.project?._id || card.project;
    if (newProjectId === currentProjectId) {
      return;
    }

    try {
      setLoadingProjectChange(true);
      setSelectedProjectForMove(newProjectId);

      // Fetch columns for the new project first
      await fetchColumns(newProjectId);

      // Move the card with auto-selected "todo" status
      const response = await cardAPI.updateProject(card._id, newProjectId);

      if (response.data.success) {
        onCardUpdated(response.data.card);

        // Find the project name for the toast and immediate display
        const targetProject = projects.find(p => p._id === newProjectId);
        const projectName = targetProject?.name || "new project";

        // Store the moved project name to update the dropdown display immediately
        setMovedProjectName(projectName);

        showToast(`Card moved to ${projectName}. Please select a column for this project.`, "success");

        // Keep selectedProjectForMove set so Status dropdown shows the new project's columns
        // This is important because the card now belongs to the new project
      }
    } catch (error) {
      console.error("Error moving card to project:", error);
      showToast("Failed to move card to project", "error");
      setSelectedProjectForMove(null);
      setSelectedProjectColumns([]);
    } finally {
      setLoadingProjectChange(false);
    }
  };

  const handleCompleteToggle = async () => {
    try {
      const response = await cardAPI.toggleComplete(card._id);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast(
          response.data.card.isComplete
            ? "Card marked as complete!"
            : "Card marked as incomplete!",
          "success"
        );
      }
    } catch (error) {
      console.error("Error toggling completion:", error);
      showToast("Failed to toggle completion", "error");
    }
  };

  // Card Items Management
  const fetchItems = async () => {
    try {
      setLoadingItems(true);
      const response = await cardItemAPI.getCardItems(card._id);
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching card items:", error);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) {
      showToast("Item title is required", "error");
      return;
    }

    try {
      const response = await cardItemAPI.createCardItem(card._id, {
        title: newItemTitle.trim(),
      });

      if (response.data.success) {
        setItems((prev) => [...prev, response.data.item]);
        setNewItemTitle("");
        setShowAddItem(false);
        showToast("Item added successfully!", "success");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      showToast("Failed to add item", "error");
    }
  };

  const handleToggleItem = async (itemId, completed) => {
    try {
      const response = await cardItemAPI.updateCardItem(card._id, itemId, {
        completed: !completed,
      });

      if (response.data.success) {
        setItems((prev) =>
          prev.map((item) =>
            item._id === itemId ? { ...item, completed: !completed } : item
          )
        );
      }
    } catch (error) {
      console.error("Error updating item:", error);
      showToast("Failed to update item", "error");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const response = await cardItemAPI.deleteCardItem(card._id, itemId);

      if (response.data.success) {
        setItems((prev) => prev.filter((item) => item._id !== itemId));
        showToast("Item deleted successfully!", "success");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showToast("Failed to delete item", "error");
    }
  };

  const handleStartEditItem = (item) => {
    setEditingItemId(item._id);
    setEditingItemTitle(item.title);
  };

  const handleCancelEditItem = () => {
    setEditingItemId(null);
    setEditingItemTitle("");
  };

  const handleSaveEditItem = async (itemId) => {
    if (!editingItemTitle.trim()) {
      showToast("Item title cannot be empty", "error");
      return;
    }

    try {
      const response = await cardItemAPI.updateCardItem(card._id, itemId, {
        title: editingItemTitle.trim(),
      });

      if (response.data.success) {
        setItems((prev) =>
          prev.map((item) =>
            item._id === itemId
              ? { ...item, title: editingItemTitle.trim() }
              : item
          )
        );
        setEditingItemId(null);
        setEditingItemTitle("");
        showToast("Item updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error updating item:", error);
      showToast("Failed to update item", "error");
    }
  };

  // Fetch columns for the project (can fetch for any project by passing projectId)
  const fetchColumns = async (projectId = null) => {
    const targetProjectId = projectId || cardProjectId || currentProject?._id;
    if (!targetProjectId) return;

    try {
      setLoadingColumns(true);
      const { columnAPI } = await import("../utils/api");
      const response = await columnAPI.getColumns(targetProjectId);
      if (response.data.success) {
        // If fetching for a project move, set selectedProjectColumns
        if (projectId && projectId !== (card.project?._id || card.project)) {
          setSelectedProjectColumns(response.data.columns);
        } else {
          // Otherwise, set the regular columns for current project
          setColumns(response.data.columns);
        }
      }
    } catch (error) {
      console.error("Error fetching columns:", error);
    } finally {
      setLoadingColumns(false);
    }
  };

  // Refresh columns when project changes
  React.useEffect(() => {
    const targetProjId = cardProjectId || currentProject?._id;
    if (targetProjId) {
      fetchColumns(targetProjId);
    }
  }, [cardProjectId, currentProject?._id]);

  // Fetch items when component mounts
  React.useEffect(() => {
    fetchItems();
  }, [card._id]);

  // The board list payload is slim (no comments/activityLog), so hydrate the
  // full card on open. Also refetch if comment user data is incomplete.
  React.useEffect(() => {
    const missingDetail = card.comments === undefined;
    const hasIncompleteUserData = card.comments?.some(
      (comment) =>
        !comment.user || typeof comment.user === "string" || !comment.user.name
    );

    if (missingDetail || hasIncompleteUserData) {
      // Refetch the card to get full comments/activity + populated user data
      const refetchCard = async () => {
        try {
          const response = await cardAPI.getCard(card._id);
          if (response.data.success) {
            onCardUpdated(response.data.card);
          }
        } catch (error) {
          console.error("Error refetching card:", error);
        }
      };
      refetchCard();
    }
  }, [card.comments, card._id, onCardUpdated]);

  // Listen for Socket.IO events for card updates
  React.useEffect(() => {
    if (!socket) return;

    const handleCardUpdated = (data) => {
      if (data.card && data.card._id === card._id) {
        // Skip if this event is from the current user (already updated via API)
        const currentUserId = user?._id || user?.id;
        if (data.userId === currentUserId) {
          return;
        }
        onCardUpdated(data.card);
      }
    };

    const handleCardCommentAdded = (data) => {
      if (data.card && data.card._id === card._id) {
        onCardUpdated(data.card);
      }
    };

    const handleCardLabelAdded = (data) => {
      if (
        data.card &&
        data.card._id === card._id &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        onCardUpdated(data.card);
      }
    };

    const handleCardLabelRemoved = (data) => {
      if (
        data.card &&
        data.card._id === card._id &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        onCardUpdated(data.card);
      }
    };

    const handleCardItemCreated = (data) => {
      if (
        data.cardId === card._id &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        setItems((prev) => [...prev, data.item]);
      }
    };

    const handleCardItemUpdated = (data) => {
      if (
        data.cardId === card._id &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        setItems((prev) =>
          prev.map((item) => (item._id === data.item._id ? data.item : item))
        );
      }
    };

    const handleCardItemDeleted = (data) => {
      if (
        data.cardId === card._id &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        setItems((prev) => prev.filter((item) => item._id !== data.itemId));
      }
    };

    const handleCardItemsReordered = (data) => {
      if (
        data.cardId === card._id &&
        data.userId !== user?._id &&
        data.userId !== user?.id
      ) {
        setItems(data.items);
      }
    };

    const handleCardsReordered = (data) => {
      if (data.cards && data.userId !== user?._id && data.userId !== user?.id) {
        const matchingCard = data.cards.find((c) => c._id === card._id);
        if (matchingCard) {
          onCardUpdated(matchingCard);
        }
      }
    };

    const handleCardRelatedUpdated = async (data) => {
      if (!data) return;
      const currentUserId = user?._id || user?.id;
      if (data.userId === currentUserId) return;

      if (data.cardId === card._id || data.relatedCardId === card._id) {
        if (data.card && data.cardId === card._id) {
          onCardUpdated(data.card);
        } else {
          try {
            const response = await cardAPI.getCard(card._id);
            if (response.data?.success) {
              onCardUpdated(response.data.card);
            }
          } catch (error) {
            console.error("Error refreshing related cards on socket event:", error);
          }
        }
      }
    };

    socket.on("card-updated", handleCardUpdated);
    socket.on("card-comment-added", handleCardCommentAdded);
    socket.on("card-comment-updated", handleCardUpdated);
    socket.on("card-label-added", handleCardLabelAdded);
    socket.on("card-label-removed", handleCardLabelRemoved);
    socket.on("card-user-assigned", handleCardUpdated);
    socket.on("card-user-unassigned", handleCardUpdated);
    socket.on("card-attachment-added", handleCardUpdated);
    socket.on("card-attachment-removed", handleCardUpdated);
    socket.on("card-files-uploaded", handleCardUpdated);
    socket.on("card-item-created", handleCardItemCreated);
    socket.on("card-item-updated", handleCardItemUpdated);
    socket.on("card-item-deleted", handleCardItemDeleted);
    socket.on("card-items-reordered", handleCardItemsReordered);
    socket.on("cards-reordered", handleCardsReordered);
    socket.on("card-related-updated", handleCardRelatedUpdated);

    return () => {
      socket.off("card-updated", handleCardUpdated);
      socket.off("card-comment-added", handleCardCommentAdded);
      socket.off("card-comment-updated", handleCardUpdated);
      socket.off("card-label-added", handleCardLabelAdded);
      socket.off("card-label-removed", handleCardLabelRemoved);
      socket.off("card-user-assigned", handleCardUpdated);
      socket.off("card-user-unassigned", handleCardUpdated);
      socket.off("card-attachment-added", handleCardUpdated);
      socket.off("card-attachment-removed", handleCardUpdated);
      socket.off("card-files-uploaded", handleCardUpdated);
      socket.off("card-item-created", handleCardItemCreated);
      socket.off("card-item-updated", handleCardItemUpdated);
      socket.off("card-item-deleted", handleCardItemDeleted);
      socket.off("card-items-reordered", handleCardItemsReordered);
      socket.off("cards-reordered", handleCardsReordered);
      socket.off("card-related-updated", handleCardRelatedUpdated);
    };
  }, [socket, card._id, onCardUpdated, user?._id, user?.id]);

  // Add selection change listener for rich text editor
  React.useEffect(() => {
    if (showRichText) {
      const handleSelectionChange = () => {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;
          const element =
            container.nodeType === Node.TEXT_NODE
              ? container.parentElement
              : container;

          setActiveFormatting({
            bold: element.closest("strong, b") !== null,
            italic: element.closest("em, i") !== null,
            underline: element.closest("u") !== null,
            header: element.closest("h1, h2, h3") !== null,
            list: element.closest("ul, ol, li") !== null,
            link: element.closest("a") !== null,
          });
        }
      };

      document.addEventListener("selectionchange", handleSelectionChange);
      return () => {
        document.removeEventListener("selectionchange", handleSelectionChange);
      };
    }
  }, [showRichText]);

  // Label Management
  const handleToggleLabel = async (label) => {
    const existingLabel = card.labels?.find((item) => item.name === label.name);
    setLabelSearchTerm("");

    try {
      const response = existingLabel
        ? await cardAPI.removeLabel(card._id, existingLabel._id || existingLabel.id)
        : await cardAPI.addLabel(card._id, {
          name: label.name,
          color: label.color || "blue",
        });

      if (response.data.success) {
        onCardUpdated(response.data.card);
        setLabelSearchTerm("");
        showToast(existingLabel ? "Label removed successfully!" : "Label added successfully!", "success");
      }
    } catch (error) {
      console.error("Error toggling label:", error);
      showToast(existingLabel ? "Failed to remove label" : "Failed to add label", "error");
    }
  };

  const handleCreateLabel = async () => {
    const projectId = card.project?._id || card.project || currentProject?._id;
    const name = newLabelName.trim();
    if (!name || !projectId) return false;

    try {
      const projectResponse = await projectAPI.createLabel(projectId, { name, color: newLabelColor });
      if (!projectResponse.data.success) return false;

      const createdLabel = projectResponse.data.label || { name, color: newLabelColor };
      setAvailableLabels((prev) => (
        prev.some((label) => label.name === createdLabel.name) ? prev : [...prev, createdLabel]
      ));

      const cardResponse = await cardAPI.addLabel(card._id, {
        name: createdLabel.name,
        color: createdLabel.color || newLabelColor,
      });

      if (cardResponse.data.success) {
        onCardUpdated(cardResponse.data.card);
        setNewLabelName("");
        setNewLabelColor("blue");
        setShowCreateLabel(false);
        setLabelSearchTerm("");
        showToast("Label created and applied successfully!", "success");
        return true;
      }
    } catch (error) {
      console.error("Error creating label:", error);
      showToast("Failed to create label", "error");
    }

    return false;
  };

  const handleUpdateLabel = async (label, updates) => {
    const projectId = card.project?._id || card.project || currentProject?._id;
    const labelId = label._id || label.id;
    if (!projectId || !labelId) {
      showToast("Cannot edit this label because its project label ID is missing", "error");
      return false;
    }

    const nextLabel = {
      name: updates.name.trim(),
      color: updates.color || label.color || "blue",
    };

    const nameChanged = nextLabel.name !== label.name;
    const colorChanged = nextLabel.color !== label.color;
    if (!nameChanged && !colorChanged) {
      showToast("No changes to save", "info");
      return true;
    }

    try {
      const response = await projectAPI.updateLabel(projectId, labelId, nextLabel);
      if (!response.data.success) {
        showToast("Failed to update label", "error");
        return false;
      }

      const updatedLabel = response.data.label || { ...label, ...nextLabel, _id: labelId };
      setAvailableLabels((prev) =>
        prev.map((item) => ((item._id || item.id) === labelId ? { ...item, ...updatedLabel } : item))
      );

      if (card.labels?.some((item) => item.name === label.name)) {
        onCardUpdated({
          ...card,
          labels: card.labels.map((item) =>
            item.name === label.name
              ? { ...item, name: updatedLabel.name, color: updatedLabel.color || "blue" }
              : item
          ),
        });
      }

      showToast("Label updated successfully!", "success");
      return true;
    } catch (error) {
      console.error("Error updating label:", error);
      showToast(error.response?.data?.message || "Failed to update label", "error");
      return false;
    }
  };

  const handleRemoveLabel = async (labelIdentifier) => {
    let labelId = typeof labelIdentifier === "object" ? (labelIdentifier._id || labelIdentifier.id) : labelIdentifier;
    if (!labelId && typeof labelIdentifier === "object" && labelIdentifier.name) {
      const match = card.labels?.find((l) => l.name === labelIdentifier.name);
      labelId = match?._id || match?.id;
    }
    if (!labelId) return;

    try {
      const response = await cardAPI.removeLabel(card._id, labelId);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast("Label removed successfully!", "success");
      }
    } catch (error) {
      console.error("Error removing label:", error);
      showToast("Failed to remove label", "error");
    }
  };

  // Attachment Management
  const handleAddAttachment = async () => {
    if (!attachmentUrl.trim()) return;

    try {
      const response = await cardAPI.addAttachment(card._id, {
        filename: attachmentUrl.split("/").pop(),
        originalName: attachmentUrl.split("/").pop(),
        mimeType: "application/octet-stream",
        size: 0,
        url: attachmentUrl,
      });

      if (response.data.success) {
        onCardUpdated(response.data.card);
        setAttachmentUrl("");
        showToast("Attachment added successfully!", "success");
      }
    } catch (error) {
      console.error("Error adding attachment:", error);
      showToast("Failed to add attachment", "error");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const response = await cardAPI.deleteAttachment(card._id, attachmentId);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast("Attachment deleted successfully!", "success");
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      showToast("Failed to delete attachment", "error");
    }
  };

  // Confirm delete attachment (shows modal)
  const confirmDeleteAttachment = async (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (!attachmentToDelete) return;
    setDeletingAttachment(true);
    try {
      await handleDeleteAttachment(attachmentToDelete.id);
      if (attachmentToDelete.closeImageModal) {
        setShowImageModal(false);
      }
      setShowDeleteAttachmentConfirm(false);
      setAttachmentToDelete(null);
    } catch (err) {
      // handleDeleteAttachment handles errors/toasts
    } finally {
      setDeletingAttachment(false);
    }
  };

  // Verify an attachment URL before opening. Shows toast on 404 or failure.
  const handleViewAttachment = async (e, url) => {
    if (e) e.preventDefault();

    if (!url) {
      showToast("Attachment not found", "error");
      return;
    }

    try {
      const res = await fetch(url, { method: "HEAD" });

      if (res?.status === 404) {
        showToast("This attachment has been removed", "error");
        return;
      }

      if (!res.ok) {
        showToast("Unable to open attachment", "error");
        return;
      }

      // Open file only if it exists
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Attachment check failed:", error);
      showToast("Attachment not found or server error", "error");
    }
  };

  // Intercept clicks on links inside the modal (e.g., comments) and
  // verify attachments before opening them. This handles comment links
  // created via dangerouslySetInnerHTML.
  useEffect(() => {
    if (!modalRef?.current) return;

    const isLikelyAttachment = (href) => {
      if (!href) return false;
      const lower = href.toLowerCase();
      // Common file/video extensions
      if (lower.match(/\.(pdf|mp4|mov|webm|mkv|avi|zip|rar|7z|docx?|xlsx?|pptx?|txt|csv)(\?|$)/)) {
        return true;
      }
      // Links to the API uploads path
      if (lower.includes('/uploads') || lower.includes(API_URL.toLowerCase())) {
        return true;
      }
      return false;
    };

    const handler = (event) => {
      const anchor = event.target.closest && event.target.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

      const fullUrl = href.startsWith('http') ? href : `${API_URL}${href}`;
      if (isLikelyAttachment(fullUrl)) {
        event.preventDefault();
        handleViewAttachment(event, fullUrl);
      }
    };

    const node = modalRef.current;
    node.addEventListener('click', handler);
    return () => node.removeEventListener('click', handler);
  }, [modalRef, handleViewAttachment]);


  const handleArchive = () => {
    setShowDeleteConfirm(true);
  };

  const confirmArchive = async () => {
    try {
      setLoading(true);
      const response = await cardAPI.archiveCard(card._id);

      if (response.data.success) {
        onCardDeleted(card._id);
        onClose();
        showToast("Card archived successfully!", "success");
      }
    } catch (error) {
      console.error("Error archiving card:", error);
      showToast("Failed to archive card", "error");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleRestore = async () => {
    try {
      setLoading(true);
      const response = await cardAPI.restoreCard(card._id);
      if (response.data.success) {
        // Update card locally first
        onCardUpdated(response.data.card);
        // Notify parent component
        if (onCardRestored) {
          onCardRestored(card._id, response.data.card);
        }
        showToast("Card restored successfully!", "success");
        // Don't close modal - let user see the restored card
      }
    } catch (error) {
      console.error("Error restoring card:", error);
      showToast("Failed to restore card", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    setShowDeleteCardConfirm(true);
  };

  const confirmDelete = async () => {
    if (!card || !card._id) {
      showToast("Card ID is missing", "error");
      setShowDeleteCardConfirm(false);
      return;
    }

    if (!card.isArchived) {
      showToast("Only archived cards can be permanently deleted", "error");
      setShowDeleteCardConfirm(false);
      return;
    }

    try {
      setLoading(true);
      console.log("Deleting card:", card._id);
      const response = await cardAPI.deleteCard(card._id);

      if (response && response.data && response.data.success) {
        // Close confirmation modal
        setShowDeleteCardConfirm(false);

        // Close card modal
        onClose();

        // Notify parent to remove card from state
        if (onCardPermanentlyDeleted) {
          await onCardPermanentlyDeleted(card._id);
        } else if (onCardDeleted) {
          await onCardDeleted(card._id);
        }

        showToast("Card deleted permanently", "success");
      } else {
        throw new Error("Delete request failed");
      }
    } catch (error) {
      console.error("Error deleting card:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to delete card. Please try again.";
      showToast(errorMessage, "error");
      // Keep modal open on error so user can try again
    } finally {
      setLoading(false);
    }
  };

  // Shared by both composing a new comment and editing an existing one:
  // uploads pending files (skipping the legacy auto-generated comment) and
  // returns HTML to merge into the comment text. Throws on failure so
  // callers can abort their save/post cleanly without losing anything.
  const uploadPendingAttachments = async (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    formData.append("skipAutoComment", "true");

    const uploadResponse = await cardAPI.uploadFiles(card._id, formData);
    if (!uploadResponse.data.success) {
      throw new Error(uploadResponse.data.message || "Upload failed");
    }

    const uploaded = uploadResponse.data.uploadedFiles || [];
    const attachmentHtml = uploaded
      .map((file) => {
        const isImage = file.mimeType?.startsWith("image/");
        return isImage
          ? `<img src="${file.url}" alt="${file.originalName}" />`
          : `<a href="${file.url}" target="_blank">${file.originalName}</a>`;
      })
      // A plain space, not <br/> — the images/links are styled
      // `inline-block` so they sit in a row and wrap naturally; a <br/>
      // here would force each one onto its own line instead.
      .join(" ");

    // Reflect the new attachments on the card immediately (counts, Images
    // grid, etc.) even before the comment itself is posted/updated.
    if (uploadResponse.data.card) {
      onCardUpdated(uploadResponse.data.card);
    }

    return attachmentHtml;
  };

  const handleAddComment = async () => {
    const plainText = (commentText || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (!plainText && pendingCommentFiles.length === 0) {
      showToast("Please write a comment", "warning");
      return;
    }
    console.log({ commentText });

    // Upload any pending attachments FIRST, in their own try/catch, so a
    // failure here (too large, too many, disallowed type, network error)
    // shows a clear message and stops before anything is posted — the
    // typed text stays in the box and the attachments stay pending so the
    // user can remove the bad one and retry, instead of losing their comment
    // or ending up with a broken/partial post.
    let attachmentHtml = "";
    if (pendingCommentFiles.length > 0) {
      setIsUploadingCommentFiles(true);
      try {
        attachmentHtml = await uploadPendingAttachments(pendingCommentFiles);
      } catch (error) {
        console.error("Error uploading comment attachments:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload attachment(s)";
        showToast(errorMessage, "error");
        setIsUploadingCommentFiles(false);
        return; // Abort — do not post a comment; nothing was lost.
      }
      setIsUploadingCommentFiles(false);
    }

    const mergedText = [commentText.trim(), attachmentHtml]
      .filter(Boolean)
      .join(commentText.trim() ? "<br/>" : "");

    try {
      // Extract mentions from the actual comment text
      const mentionRegex = /@([A-Za-z0-9._]+)/g;
      const mentionMatches = [...commentText.matchAll(mentionRegex)];

      // Debugging logs for mentions and project members
      // console.log("Comment text:", commentText);
      // console.log("Mention matches:", mentionMatches);
      // console.log("Current project members:", currentProject?.members);

      // Find users for each mention in the text
      const actualMentions = mentionMatches
        .map((match) => {
          const username = match[1].toLowerCase();

          // Debugging log for each username found in the comment
          // console.log("Looking for username:", username);

          // Find the user in project members
          const member = currentProject?.members?.find((m) => {
            if (!m.user) return false;
            const memberUsername = m.user.name
              .toLowerCase()
              .replace(/\s+/g, "");

            // Debugging log for each comparison
            // console.log(
            //   "Comparing with member:",
            //   memberUsername,
            //   "against:",
            //   username
            // );

            return memberUsername === username;
          });

          if (member && member.user) {

            // Debugging log for found member
            // console.log("Found member:", member.user);

            // Return user object with required fields
            return {
              id: ["allmembersonthecard", "allmembersontheboard"].includes(
                username
              )
                ? "card"
                : member.user._id,
              name: member.user.name,
              email: member.user.email,
              avatar: member.user.avatar,
              color: member.user.color,
              type: ["allmembersonthecard", "allmembersontheboard"].includes(
                username
              )
                ? "group"
                : "user",
            };
          }

          console.log("Member not found for username:", username);
          return null;
        })
        .filter((user) => user !== null); // Remove null entries

      // console.log("Actual mentions to send:", actualMentions);

      const response = await cardAPI.addComment(
        card._id,
        mergedText,
        actualMentions
      );

      if (response.data.success) {
        onCardUpdated(response.data.card);
        setCommentText("");
        setMentions([]);
        setPendingCommentFiles([]);
        showToast("Comment added successfully!", "success");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      console.error("Error response:", error.response?.data);
      // The attachment (if any) already uploaded successfully and is safely
      // on the card's attachments list — only the comment text failed to
      // post, so surface that precisely rather than implying data was lost.
      showToast(
        pendingCommentFiles.length > 0
          ? "Attachment uploaded, but the comment text failed to post — please try again"
          : "Failed to add comment",
        "error"
      );
    }
  };

  const handleUpdateComment = async (commentId, newText) => {
    try {
      const response = await cardAPI.updateComment(
        card._id,
        commentId,
        newText
      );

      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast("Comment updated successfully!", "success");
        setEditingComment(null);
        setEditCommentText("");
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      showToast("Failed to update comment", "error");
    }
  };

  const handleStartEditComment = (comment) => {
    // Prevent editing system-generated comments
    if (isLegacySystemComment(comment)) {
      showToast("Cannot edit system-generated comments", "error");
      return;
    }
    setEditingComment(comment._id || comment.id);
    // Resolve relative /uploads/... paths to absolute so any preserved
    // attachment preview can actually load (mirrors the read-only display
    // path, which does the same before rendering).
    const resolvedHtml = resolveAttachmentUrls(comment.text || "");
    // Keep existing images/files OUT of the editable area — edit mode is
    // text-only for now; pull them out here and re-attach untouched on save,
    // so they're never lost, just not directly editable yet.
    const existingAttachments =
      resolvedHtml.match(/<img[^>]*>|<a[^>]*>.*?<\/a>/g) || [];
    const textOnly = resolvedHtml
      .replace(/<img[^>]*>|<a[^>]*>.*?<\/a>/g, "")
      .trim();
    setEditCommentText(textOnly);
    setEditExistingAttachmentHtml(existingAttachments.join(" "));
    setEditPendingFiles([]);
  };

  const handleCancelEditComment = () => {
    setEditingComment(null);
    setEditCommentText("");
    setEditPendingFiles([]);
    setEditExistingAttachmentHtml("");
  };

  const handleEditFileSelect = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (editPendingFiles.length + files.length > 5) {
      showToast("You can attach up to 5 files to a comment", "error");
      return;
    }
    const tooBig = files.find((f) => f.size > 25 * 1024 * 1024);
    if (tooBig) {
      showToast(`File "${tooBig.name}" size must be less than 25MB`, "error");
      return;
    }
    setEditPendingFiles((prev) => [...prev, ...files]);
  };

  const removeEditPendingFile = (index) => {
    setEditPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveEditComment = async () => {
    const plainEdit = (editCommentText || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    if (!plainEdit && editPendingFiles.length === 0 && !editExistingAttachmentHtml) {
      showToast("Please write a comment", "warning");
      return;
    }
    let mergedText = editCommentText.trim();

    // Same careful, isolated error handling as the compose flow: a failed
    // upload aborts the save entirely — nothing is updated, the typed edit
    // and pending attachments stay put so the user can fix and retry.
    if (editPendingFiles.length > 0) {
      setIsUploadingEditFiles(true);
      try {
        const attachmentHtml = await uploadPendingAttachments(editPendingFiles);
        mergedText = [mergedText, attachmentHtml]
          .filter(Boolean)
          .join(mergedText ? "<br/>" : "");
      } catch (error) {
        console.error("Error uploading edit attachments:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload attachment(s)";
        showToast(errorMessage, "error");
        setIsUploadingEditFiles(false);
        return;
      }
      setIsUploadingEditFiles(false);
    }

    // Re-attach the existing images/files that were kept out of the editable
    // area, untouched — the text is what changed, the attachments weren't
    // meant to be editable in this pass.
    if (editExistingAttachmentHtml) {
      mergedText = [mergedText, editExistingAttachmentHtml]
        .filter(Boolean)
        .join(mergedText ? "<br/>" : "");
    }

    if (!mergedText.trim()) {
      showToast("Please write a comment", "warning");
      return;
    }
    setEditPendingFiles([]);
    setEditExistingAttachmentHtml("");
    // Store relative paths (like every other comment), not the absolute
    // ones the edit box needed just to render existing images.
    handleUpdateComment(editingComment, unresolveAttachmentUrls(mergedText));
  };

  // Reply Handlers for Jira-style threaded comments
  const handleStartReply = (comment) => {
    setReplyingToCommentId(comment._id || comment.id);
    const author = comment.user || {};
    setReplyToUser(author);
    const authorName = author.name || "User";
    const authorId = author._id || author.id || "";
    const cleanName = authorName.replace(/\s+/g, "").toLowerCase();
    const color = author.color || "#2563eb";
    const rgb = hexToRgb(color);
    const bg = `rgba(${rgb.r},${rgb.g},${rgb.b},0.14)`;
    const border = `rgba(${rgb.r},${rgb.g},${rgb.b},0.24)`;
    const hoverBg = `rgba(${rgb.r},${rgb.g},${rgb.b},0.22)`;

    // Render as a styled mention span blot matching SimpleCommentEditor format
    const mentionHtml = `<p><span class="mention" data-user-id="${authorId}" data-user-name="${authorName}" style="--mention-color: ${color}; --mention-bg: ${bg}; --mention-border: ${border}; --mention-hover-bg: ${hoverBg};">@${cleanName}</span>&nbsp;</p>`;

    setReplyCommentText(mentionHtml);
    setReplyMentions(authorId ? [{ id: authorId, name: authorName, type: "user" }] : []);
    setReplyPendingFiles([]);
  };

  const handleCancelReply = () => {
    setReplyingToCommentId(null);
    setReplyToUser(null);
    setReplyCommentText("");
    setReplyMentions([]);
    setReplyPendingFiles([]);
  };

  const handleReplyFileSelect = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (replyPendingFiles.length + files.length > 5) {
      showToast("You can attach up to 5 files to a reply", "error");
      return;
    }
    const tooBig = files.find((f) => f.size > 25 * 1024 * 1024);
    if (tooBig) {
      showToast(`File "${tooBig.name}" size must be less than 25MB`, "error");
      return;
    }
    setReplyPendingFiles((prev) => [...prev, ...files]);
  };

  const removeReplyPendingFile = (index) => {
    setReplyPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddReply = async (parentCommentId) => {
    const plainText = (replyCommentText || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    if (!plainText && replyPendingFiles.length === 0) {
      showToast("Please write a reply", "warning");
      return;
    }

    let attachmentHtml = "";
    if (replyPendingFiles.length > 0) {
      setIsUploadingReplyFiles(true);
      try {
        attachmentHtml = await uploadPendingAttachments(replyPendingFiles);
      } catch (error) {
        console.error("Error uploading reply attachments:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to upload attachment(s)";
        showToast(errorMessage, "error");
        setIsUploadingReplyFiles(false);
        return;
      }
      setIsUploadingReplyFiles(false);
    }

    const mergedText = [replyCommentText.trim(), attachmentHtml]
      .filter(Boolean)
      .join(replyCommentText.trim() ? "<br/>" : "");

    try {
      const mentionRegex = /@([A-Za-z0-9._]+)/g;
      const mentionMatches = [...replyCommentText.matchAll(mentionRegex)];

      const actualMentions = mentionMatches
        .map((match) => {
          const username = match[1].toLowerCase();
          const member = currentProject?.members?.find((m) => {
            if (!m.user) return false;
            const memberUsername = m.user.name
              .toLowerCase()
              .replace(/\s+/g, "");
            return memberUsername === username;
          });

          if (member && member.user) {
            return {
              id: member.user._id,
              name: member.user.name,
              email: member.user.email,
              avatar: member.user.avatar,
              color: member.user.color,
              type: "user",
            };
          }
          return null;
        })
        .filter(Boolean);

      const response = await cardAPI.addComment(
        card._id,
        mergedText,
        actualMentions,
        parentCommentId
      );

      if (response.data.success) {
        onCardUpdated(response.data.card);
        handleCancelReply();
        showToast("Reply posted successfully!", "success");
      }
    } catch (error) {
      console.error("Error adding reply:", error);
      showToast("Failed to post reply", "error");
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await cardAPI.deleteComment(card._id, commentId);
      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast("Comment deleted successfully!", "success");
        setShowDeleteCommentConfirm(false);
        setCommentToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      showToast("Failed to delete comment", "error");
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
    // Reset the input value to allow selecting the same files again
    event.target.value = "";
  };

  const isImageAttachment = (attachment) => {
    return (
      attachment.type === "image" ||
      attachment.mimeType?.startsWith("image/") ||
      (attachment.originalName &&
        /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(attachment.originalName)) ||
      (attachment.filename &&
        /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(attachment.filename))
    );
  };

  const getImageAttachments = () => {
    return card.attachments.filter(isImageAttachment);
  };

  const getOtherAttachments = () => {
    return card.attachments.filter(
      (attachment) => !isImageAttachment(attachment)
    );
  };

  const getFileIcon = (attachment) => {
    const mimeType = attachment.mimeType || "";
    const fileName =
      attachment.originalName || attachment.filename || attachment.name || "";

    if (mimeType.includes("pdf")) return "📄";
    if (mimeType.includes("word") || fileName.match(/\.(doc|docx)$/i))
      return "📝";
    if (mimeType.includes("excel") || fileName.match(/\.(xls|xlsx)$/i))
      return "📊";
    if (mimeType.includes("powerpoint") || fileName.match(/\.(ppt|pptx)$/i))
      return "📋";
    if (mimeType.includes("zip") || fileName.match(/\.(zip|rar|7z)$/i))
      return "🗜️";
    if (mimeType.includes("text") || fileName.match(/\.(txt|csv)$/i))
      return "📄";
    if (mimeType.includes("json") || fileName.match(/\.json$/i)) return "🔧";
    if (mimeType.includes("xml") || fileName.match(/\.xml$/i)) return "📋";

    return "📎"; // Default file icon
  };

  const handleSetPriority = async (priority) => {
    try {
      // Update the card with the new priority
      const updateData = {
        title: card.title,
        description: card.description,
        dueDate: card.dueDate || null,
        priority: priority,
      };

      const response = await cardAPI.updateCard(card._id, updateData);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast(`Priority set to ${priority}`, "success");
      }
    } catch (error) {
      console.error("Error setting priority:", error);
      showToast("Failed to update priority", "error");
    }
  };

  const handleUnassignUser = async (userId) => {
    if (!userId) return;

    setIsUnassigning(true);
    const prevAssignees = card.assignees || [];
    // Optimistically remove assignee immediately
    onCardUpdated({
      ...card,
      assignees: prevAssignees.filter((a) => (a._id || a.id || a) !== userId),
    });

    try {
      const response = await cardAPI.unassignUser(card._id, userId);

      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast("User unassigned successfully!", "success");
      } else {
        onCardUpdated({ ...card, assignees: prevAssignees });
      }
    } catch (error) {
      console.error("Error unassigning user:", error);
      onCardUpdated({ ...card, assignees: prevAssignees });
      showToast("Failed to unassign user", "error");
    } finally {
      setIsUnassigning(false);
      setShowUnassignConfirm(false);
      setUserToUnassign(null);
    }
  };

  const handleToggleAssignee = async (member) => {
    const memberId = member._id || member.id;
    if (!memberId) return;

    setMemberSearch("");
    const isAssigned = selectedAssigneeIds.includes(memberId);
    const prevAssignees = card.assignees || [];

    try {
      if (isAssigned) {
        // Optimistically remove assignee immediately
        onCardUpdated({
          ...card,
          assignees: [],
        });
        const response = await cardAPI.unassignUser(card._id, memberId);
        if (response.data.success) {
          onCardUpdated(response.data.card);
          showToast("User unassigned successfully!", "success");
        } else {
          onCardUpdated({ ...card, assignees: prevAssignees });
        }
        return;
      }

      // Optimistically set the newly assigned user immediately
      const newAssignee = {
        _id: memberId,
        id: memberId,
        name: member.name,
        email: member.email,
        avatar: member.avatar,
        color: member.color || "#3b82f6",
      };
      onCardUpdated({
        ...card,
        assignees: [newAssignee],
      });

      // Single-assignee model: swap out any existing assignee for the new one
      const previousAssigneeId = selectedAssigneeIds[0];
      if (previousAssigneeId) {
        await cardAPI.unassignUser(card._id, previousAssigneeId);
      }

      const response = await cardAPI.assignUser(card._id, memberId);
      if (response.data.success) {
        onCardUpdated(response.data.card);
        showToast("User assigned to card successfully!", "success");
      } else {
        onCardUpdated({ ...card, assignees: prevAssignees });
      }
    } catch (error) {
      console.error("Error toggling assignee:", error);
      onCardUpdated({ ...card, assignees: prevAssignees });
      showToast(isAssigned ? "Failed to unassign user" : "Failed to assign user", "error");
    }
  };

  // Markdown formatting helpers

  // Convert HTML back to markdown with better handling

  const assignees = getAssignees();
  const selectedAssigneeIds = assignees.map((member) => member._id || member.id).filter(Boolean);
  const currentUserId = user?._id || user?.id;
  const isCurrentUserAssigned = Boolean(
    currentUserId && selectedAssigneeIds.includes(currentUserId)
  );
  const assigneeButtonLabel = isCurrentUserAssigned ? "Change" : "Assign to me";
  const projectMembers =
    currentProject?.members
      ?.map((member) => {
        if (!member) return null;
        if (typeof member === "string") {
          return users.find(
            (user) => user._id === member || user.id === member
          );
        }
        const resolvedUser = member.user
          ? users.find(
            (user) => user._id === member.user || user.id === member.user
          ) || member.user
          : member;
        return resolvedUser ? { ...resolvedUser, role: member.role || resolvedUser.role } : null;
      })
      .filter(Boolean) || [];
  // Navigation helpers for next/prev cards in the same column.
  // Memoized: with thousands of cards (e.g. after a large import), recomputing
  // this filter+sort on every render (it was called 3x per render, unmemoized)
  // was the dominant cost behind the modal feeling laggy to open/interact with.
  // Same filter/sort/index logic as before — only *when* it recomputes changed.
  const sameColumnCards = React.useMemo(() => {
    if (!card || !cards || cards.length === 0) return [];

    // Filter cards with the same status as current card
    const sameStatusCards = cards.filter((c) => {
      // Handle archived cards
      if (card.isArchived || card.status === "archive") {
        return c.isArchived === true;
      }
      // For non-archived cards, match status and ensure not archived
      return c.status === card.status && c.isArchived !== true;
    });

    // Sort by cardNumber or position if available
    return sameStatusCards.sort((a, b) => {
      if (a.cardNumber && b.cardNumber) {
        return a.cardNumber - b.cardNumber;
      }
      // Fallback to creation date or ID
      return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, card?.status, card?.isArchived]);

  const currentCardIndex = React.useMemo(
    () => sameColumnCards.findIndex((c) => c._id === card._id),
    [sameColumnCards, card?._id],
  );

  const getNextCard = () => {
    if (
      currentCardIndex >= 0 &&
      currentCardIndex < sameColumnCards.length - 1
    ) {
      return sameColumnCards[currentCardIndex + 1];
    }
    return null;
  };

  const getPrevCard = () => {
    if (currentCardIndex > 0) {
      return sameColumnCards[currentCardIndex - 1];
    }
    return null;
  };

  const handleNavigateToCard = async (targetCard) => {
    if (targetCard && onNavigateCard) {
      try {
        // Fetch full card data from API
        const response = await cardAPI.getCard(targetCard._id);
        if (response.data.success) {
          // Use the fetched card data with all populated fields
          onNavigateCard(response.data.card);
        } else {
          // Fallback to the card from the list if API fails
          onNavigateCard(targetCard);
        }
      } catch (error) {
        console.error("Error fetching card data:", error);
        // Fallback to the card from the list if API fails
        onNavigateCard(targetCard);
      }
    }
  };

  const nextCard = getNextCard();
  const prevCard = getPrevCard();

  // Calculate display time (ticking if timer is running)
  const isTimerRunningOnThisCard = activeTimer &&
    activeTimer.isRunning &&
    String(activeTimer.cardId || activeTimer.card?._id) === String(card._id);

  const displayTime = isTimerRunningOnThisCard
    ? (card.totalTimeSpent || 0) + (activeTimer.elapsedSeconds || 0)
    : (card.totalTimeSpent || 0);

  // Image carousel helpers
  const imageAttachments = getImageAttachments();
  const previewImages =
    imageAttachments.length > 0
      ? imageAttachments
      : selectedImage
      ? [selectedImage]
      : [];
  const previewImageIndex = imageAttachments.length > 0 ? selectedImageIndex : 0;

  const showImageAt = (index) => {
    const images = getImageAttachments();
    if (!images || images.length === 0) return;
    const idx = ((index % images.length) + images.length) % images.length;
    setSelectedImageIndex(idx);
    setSelectedImage(images[idx]);
    setShowImageModal(true);
  };

  return (
    <>
      {/* Rich Text Editor Styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .rich-text-editor {
            outline: none !important;
            border: none !important;
            resize: none !important;
          }
          .rich-text-editor:focus {
            outline: none !important;
            border: none !important;
          }
          .rich-text-editor strong {
            font-weight: bold !important;
          }
          .rich-text-editor em {
            font-style: italic !important;
          }
          .rich-text-editor h1, .rich-text-editor h2, .rich-text-editor h3 {
            font-weight: bold !important;
            margin: 0.5rem 0 !important;
          }
          .rich-text-editor h1 {
            font-size: 1.5rem !important;
          }
          .rich-text-editor h2 {
            font-size: 1.25rem !important;
          }
          .rich-text-editor h3 {
            font-size: 1.125rem !important;
          }
          .rich-text-editor ul, .rich-text-editor ol,
          .rich-text-content ul, .rich-text-content ol,
          .markdown-preview ul, .markdown-preview ol {
            margin: 0.5rem 0 !important;
            padding-left: 1.5rem !important;
          }
          .rich-text-editor ul,
          .rich-text-content ul,
          .markdown-preview ul {
            list-style-type: disc !important;
          }
          .rich-text-editor ol,
          .rich-text-content ol,
          .markdown-preview ol {
            list-style-type: decimal !important;
          }
          .rich-text-editor li,
          .rich-text-content li,
          .markdown-preview li {
            list-style-position: outside !important;
            margin: 0.25rem 0 !important;
          }
          .rich-text-editor a {
            color: #2563eb !important;
            text-decoration: underline !important;
          }
          .rich-text-editor hr {
            border: 1px solid #d1d5db !important;
            margin: 1rem 0 !important;
          }
          .rich-text-editor p {
            margin: 0.5rem 0 !important;
          }
          .markdown-preview strong {
            font-weight: bold !important;
          }
          .markdown-preview em {
            font-style: italic !important;
          }
          .markdown-preview h1, .markdown-preview h2, .markdown-preview h3 {
            font-weight: bold !important;
            margin: 0.5rem 0 !important;
          }
          .markdown-preview li {
            list-style-type: disc !important;
            margin-left: 1rem !important;
          }
          .markdown-preview a {
            color: #2563eb !important;
            text-decoration: underline !important;
          }
          .markdown-preview hr {
            border: 1px solid #d1d5db !important;
            margin: 1rem 0 !important;
          }
        `,
        }}
      />

      <div className="modal-overlay">
        <div
          ref={modalRef}
          className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-w-[1400px] max-h-[100vh] overflow-hidden relative transition-colors duration-200"
        >
          {/* Modal Header */}
          <div className="bg-[#f1f2f4] dark:bg-slate-900 px-4 py-2 flex items-start transition-colors duration-200 border-b border-gray-200 dark:border-slate-700 box-border">
            <div className="flex items-start justify-between w-full gap-4">
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="w-6 h-6 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px] flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[18px] leading-none">📋</span>
                </div>
                <div className="flex-1 min-w-0">
                  {isEditingTitle ? (
                    <div className="flex items-start space-x-2 w-full">
                      <textarea
                        ref={titleTextareaRef}
                        value={formData.title}
                        onChange={(e) => {
                          setFormData({ ...formData, title: e.target.value });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSave();
                          } else if (e.key === "Escape") {
                            setIsEditingTitle(false);
                            setFormData({ ...formData, title: card.title });
                          }
                        }}
                        rows={1}
                        className="bg-white dark:bg-slate-800 border border-blue-500 rounded-md px-2 py-0.5 outline-none text-[24px] font-semibold text-gray-900 dark:text-white placeholder-gray-400 flex-1 min-w-0 resize-none overflow-hidden break-words leading-[1.3] shadow-sm max-h-[250px] overflow-y-auto"
                        placeholder="Card title..."
                        autoFocus
                      />
                      <div className="flex items-center space-x-1 shrink-0 mt-0.5">
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={loading}
                          className="w-8 h-8 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 !cursor-pointer flex items-center justify-center"
                          title="Save title"
                        >
                          <Check className="w-4 h-4 pointer-events-none" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingTitle(false);
                            setFormData({ ...formData, title: card.title });
                          }}
                          className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors shadow-sm !cursor-pointer flex items-center justify-center"
                          title="Cancel"
                        >
                          <X className="w-4 h-4 pointer-events-none" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <h2
                        className="text-[24px] font-semibold text-gray-900 dark:text-white leading-[1.3] cursor-pointer break-words transition-colors"
                        onClick={() => setIsEditingTitle(true)}
                        title="Click to edit title"
                      >
                        {card.title}
                      </h2>
                      <span className="text-[11px] text-gray-400 dark:text-slate-500 font-normal leading-none mt-0.5 select-none cursor-pointer" onClick={() => setIsEditingTitle(true)}>
                        Click to edit
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0 mt-0.5">
                {/* Navigation Buttons */}
                <div className="flex items-center h-[34px] rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
                  <button
                    onClick={() => handleNavigateToCard(prevCard)}
                    disabled={!prevCard}
                    className={`flex items-center gap-1 px-2.5 h-8 text-xs font-medium border-r border-gray-200 dark:border-slate-600 transition-colors duration-200 ${prevCard
                      ? "text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      : "text-gray-300 dark:text-slate-600 cursor-not-allowed"
                      }`}
                    title={prevCard ? "Previous card" : "No previous card"}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span>Prev</span>
                  </button>
                  <button
                    onClick={() => handleNavigateToCard(nextCard)}
                    disabled={!nextCard}
                    className={`flex items-center gap-1 px-2.5 h-8 text-xs font-medium transition-colors duration-200 ${nextCard
                      ? "text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                      : "text-gray-300 dark:text-slate-600 cursor-not-allowed"
                      }`}
                    title={nextCard ? "Next card" : "No next card"}
                  >
                    <span>Next</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => toggleBookmark(card)}
                  className={`w-8 h-8 rounded-lg border !cursor-pointer transition-all duration-200 flex items-center justify-center ${
                    bookmarked
                      ? "border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/30 text-amber-500 hover:bg-amber-100"
                      : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-400 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-slate-700"
                  }`}
                  title={bookmarked ? "Remove bookmark" : "Bookmark card"}
                >
                  <Bookmark className={`w-4 h-4 pointer-events-none ${bookmarked ? "fill-amber-500 text-amber-500" : ""}`} />
                </button>

                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-lg border border-transparent hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors text-gray-600 dark:text-slate-400 flex items-center justify-center !cursor-pointer"
                  title="Close"
                >
                  <X className="w-4 h-4 pointer-events-none" />
                </button>
              </div>
            </div>
          </div>

          {/* Modal Content */}
          <div className="py-2 px-4 max-h-[calc(100vh-50px)] ">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-4 max-h-[85vh] overflow-y-auto px-2">
                {/* Status and Due Date Display */}

                <div className="sticky top-0 z-50 bg-white dark:bg-slate-800 transition-colors duration-200 pb-4">
                  {/* Status/Due Row */}
                  <div className="flex items-center gap-2">
                    {/* Completion Toggle - Hidden when archived */}
                    {!isArchived && (
                      <button
                        onClick={handleCompleteToggle}
                        className={`mt-6 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${card.isComplete
                          ? "bg-green-500 border-green-500 hover:bg-green-600"
                          : "border-gray-400 dark:border-slate-500 hover:border-gray-600 dark:hover:border-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                          } cursor-pointer`}
                        title={
                          card.isComplete
                            ? "Mark as incomplete"
                            : "Mark as complete"
                        }
                      >
                        {card.isComplete && (
                          <svg
                            className="w-5 h-5 text-white"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                    <div className="flex items-center gap-2">
                      {/* CARD NUMBER */}
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">Card Id</span>

                        <div
                          className="h-8 min-h-[32px] max-h-[32px] min-w-[60px] px-2.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700/50
                                     text-[12px] font-medium shadow-sm flex items-center justify-center text-center whitespace-nowrap shrink-0"
                        >
                          #{card.cardNumber || card._id?.slice(-4) || "0000"}
                        </div>
                      </div>

                      {/* STATUS */}
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">Status</span>

                        {(() => {
                          const colors = getCardStatusColors(card.status);
                          const label = card?.statusLabel;

                          return (
                            <div
                              className="h-8 min-h-[32px] max-h-[32px] min-w-[60px] px-2.5 rounded-lg text-[12px] font-medium shadow-sm
                                bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700/50 flex items-center justify-center text-center whitespace-nowrap shrink-0"
                            >
                              <span
                                style={{ backgroundColor: colors.dotColor || "#10b981" }}
                              />
                              {label}
                            </div>
                          );
                        })()}
                      </div>

                      {/* DUE DATE */}
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">Due Date</span>

                        <div
                          className={`h-8 min-h-[32px] max-h-[32px] min-w-[60px] px-2.5 rounded-lg text-[12px] font-medium shadow-sm flex items-center justify-center text-center whitespace-nowrap shrink-0
                            ${card.dueDate
                              ? "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700/50"
                              : "bg-gray-100 text-gray-600 border border-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                            }`}
                        >
                          {card.dueDate
                            ? new Date(card.dueDate).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                            : "No due date"}
                        </div>
                      </div>

                      {/* CREATED BY */}
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">Created</span>

                        {(() => {
                          const created = card.createdAt ? new Date(card.createdAt) : null;
                          const createdDateStr = created
                            ? `${created.getDate()} ${created.toLocaleDateString("en-US", { month: "short" })}, ${created.getFullYear()}`
                            : null;
                          const createdTimeStr = created
                            ? created.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
                            : null;

                          return (
                            <div className="h-8 min-h-[32px] max-h-[32px] min-w-[60px] px-2.5 rounded-lg bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 text-[12px] font-medium shadow-sm flex items-center justify-center text-center whitespace-nowrap shrink-0">
                              {card.createdBy?.name || "Unknown"}
                              {created && (
                                <>
                                  {" "}· {createdDateStr}, {createdTimeStr}
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* TOTAL TIME SPENT */}
                      {features.timeTracking && (
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">Total Time</span>

                          <div className="h-8 min-h-[32px] max-h-[32px] min-w-[60px] px-2.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700/50 text-[12px] font-medium shadow-sm flex items-center justify-center text-center whitespace-nowrap shrink-0">
                            {formatDuration(displayTime)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-300">
                      <AlignLeft className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                      Description
                    </label>

                    {!isEditingDescription && (
                      <button
                        onClick={() => setIsEditingDescription(true)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium flex items-center space-x-1"
                      >
                        {/* <Edit2 className="w-4 h-4" />
                        <span>Edit</span> */}
                      </button>
                    )}
                  </div>

                  {/* Description Content */}
                  <div className="relative">
                    {isEditingDescription ? (
                      <div className="simple-quill-editor description-editor ">
                        <SimpleQuillEditor
                          value={formData.description || ""}
                          onChange={(content) => {
                            setFormData({
                              ...formData,
                              description: content,
                            });
                          }}
                          placeholder="Add a description..."
                          height="auto"
                        />
                      </div>
                    ) : (
                      <div
                        className="w-full p-4 min-h-[120px] border border-gray-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
                        onClick={() => setIsEditingDescription(true)}
                      >
                        {formData.description && formData.description.trim() ? (
                          <div
                            className="prose prose-sm dark:prose-invert max-w-none rich-text-content"
                            style={{
                              fontSize: "16px",
                              lineHeight: "1.5",
                              letterSpacing: "0.3px",
                              color: "inherit",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: formData.description,
                            }}
                          />
                        ) : (
                          <div className="flex items-center text-gray-500 dark:text-slate-500 text-sm">
                            <Edit2 className="w-4 h-4 mr-2" />
                            <span>Click to edit</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* AI Icon — bottom-right of description box, only when not editing */}
                    {!isEditingDescription && (
                      <div className="absolute bottom-2 right-2" ref={aiMenuRef}>
                        <button
                          id="description-ai-btn"
                          title="AI Transform"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAiMenuOpen((prev) => !prev);
                          }}
                          disabled={aiLoading}
                          className="
                            w-7 h-7 rounded-full flex items-center justify-center
                            bg-gradient-to-br from-violet-500 to-indigo-600
                            text-white shadow-md
                            hover:from-violet-600 hover:to-indigo-700
                            transition-all duration-200
                            disabled:opacity-50 disabled:cursor-not-allowed
                            !cursor-pointer
                          "
                        >
                          {aiLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="w-3.5 h-3.5" />
                          )}
                        </button>

                        {/* AI mode popup menu */}
                        {aiMenuOpen && (
                          <div
                            className="
                              absolute bottom-9 right-0 z-50
                              bg-white dark:bg-slate-800
                              border border-gray-200 dark:border-slate-600
                              rounded-xl shadow-xl
                              w-52 py-1.5
                              animate-fadeIn
                            "
                            onClick={(e) => e.stopPropagation()}
                          >
                            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-slate-500 select-none">
                              AI Transform
                            </p>
                            <button
                              id="ai-mode-developer"
                              className="
                                w-full text-left px-3 py-2 text-sm
                                text-gray-700 dark:text-slate-200
                                hover:bg-violet-50 dark:hover:bg-slate-700
                                flex items-center gap-2.5
                                transition-colors duration-150
                                !cursor-pointer
                              "
                              onClick={async () => {
                                setAiMenuOpen(false);
                                if (!formData.description?.trim()) return;
                                setAiLoading(true);
                                try {
                                  const res = await aiAPI.transformDescription(formData.description, "developer");
                                  if (res.data?.success) {
                                    setAiConvertedDescription({ mode: "developer", content: res.data.result });
                                  }
                                } catch (err) {
                                  console.error("AI transform error:", err);
                                } finally {
                                  setAiLoading(false);
                                }
                              }}
                            >
                              <span className="text-base">🧑‍💻</span>
                              <div>
                                <div className="font-medium leading-tight">Developer Mode</div>
                                <div className="text-[11px] text-gray-400 dark:text-slate-400 leading-tight">Structured technical breakdown</div>
                              </div>
                            </button>
                            <button
                              id="ai-mode-summary"
                              className="
                                w-full text-left px-3 py-2 text-sm
                                text-gray-700 dark:text-slate-200
                                hover:bg-violet-50 dark:hover:bg-slate-700
                                flex items-center gap-2.5
                                transition-colors duration-150
                                !cursor-pointer
                              "
                              onClick={async () => {
                                setAiMenuOpen(false);
                                if (!formData.description?.trim()) return;
                                setAiLoading(true);
                                try {
                                  const res = await aiAPI.transformDescription(formData.description, "summary");
                                  if (res.data?.success) {
                                    setAiConvertedDescription({ mode: "summary", content: res.data.result });
                                  }
                                } catch (err) {
                                  console.error("AI transform error:", err);
                                } finally {
                                  setAiLoading(false);
                                }
                              }}
                            >
                              <span className="text-base">📝</span>
                              <div>
                                <div className="font-medium leading-tight">Summary Mode</div>
                                <div className="text-[11px] text-gray-400 dark:text-slate-400 leading-tight">Concise plain-language overview</div>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons - Only show when editing description */}
                  {isEditingDescription && (
                    <div className="mt-3 flex items-center justify-end space-x-2">
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="h-8 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 rounded-lg transition-colors duration-200 flex items-center space-x-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed !cursor-pointer"
                      >
                        <Save className="w-4 h-4" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingDescription(false);
                          // Reset to original description if user cancels
                          setFormData({
                            ...formData,
                            description: card.description,
                          });
                        }}
                        className="h-8 text-gray-700 dark:text-slate-400 hover:text-gray-800 hover:bg-gray-100 dark:hover:text-slate-200 font-medium border border-gray-300 dark:border-slate-600 px-4 rounded-lg transition-colors duration-200 text-sm flex items-center justify-center !cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {/* AI Converted Description — shown below original, read-only */}
                  {aiConvertedDescription && (
                    <div className="mt-4 rounded-xl border border-violet-200 dark:border-violet-800/50 bg-violet-50/60 dark:bg-violet-950/20 overflow-hidden">
                      {/* Header */}
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-violet-200 dark:border-violet-800/40 bg-violet-100/50 dark:bg-violet-900/20">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-2.5 h-2.5 text-white" />
                          </div>
                          <span className="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide">
                            {aiConvertedDescription.mode === "developer" ? "🧑‍💻 Developer Mode" : "📝 Summary Mode"}
                          </span>
                          <span className="text-[10px] text-violet-400 dark:text-violet-500 font-medium">AI Generated · Read-only</span>
                        </div>
                        <button
                          id="ai-converted-remove-btn"
                          title="Remove AI output"
                          onClick={() => setAiConvertedDescription(null)}
                          className="
                            w-6 h-6 rounded-full flex items-center justify-center
                            text-violet-400 hover:text-violet-600 dark:text-violet-500 dark:hover:text-violet-300
                            hover:bg-violet-200 dark:hover:bg-violet-800/50
                            transition-colors duration-150
                            !cursor-pointer
                          "
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {/* Content */}
                      <div
                        className="px-4 py-3 prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-slate-300"
                        style={{ fontSize: "13px", lineHeight: "1.65" }}
                        dangerouslySetInnerHTML={{
                          __html: aiConvertedDescription.content
                            /* Convert markdown bold */
                            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
                            /* Convert unordered list items */
                            .replace(/^[ \t]*[-*][ \t]+(.+)$/gm, "<li>$1</li>")
                            /* Wrap consecutive <li> groups in <ul> */
                            .replace(/(<li>.*<\/li>\n?)+/gs, (m) => `<ul class="list-disc pl-5 my-1">${m}</ul>`)
                            /* Convert ordered list items */
                            .replace(/^[ \t]*\d+\.[ \t]+(.+)$/gm, "<oli>$1</oli>")
                            .replace(/(<oli>.*<\/oli>\n?)+/gs, (m) =>
                              `<ol class="list-decimal pl-5 my-1">${m.replace(/<oli>/g, "<li>").replace(/<\/oli>/g, "</li>")}</ol>`
                            )
                            /* Convert markdown headings (##, ###) */
                            .replace(/^#{1,2}[ \t]+(.+)$/gm, "<h3 class=\"font-semibold text-sm mt-2 mb-0.5\">$1</h3>")
                            .replace(/^###[ \t]+(.+)$/gm, "<h4 class=\"font-semibold text-xs mt-2 mb-0.5 uppercase tracking-wide\">$1</h4>")
                            /* Convert line breaks to <br> for plain paragraphs */
                            .replace(/\n{2,}/g, "<br/>")
                            .replace(/\n/g, "<br/>")
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Checklist */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-[14px] font-semibold text-gray-700 dark:text-slate-300">
                      <CheckSquare className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                      Checklist ({items.length})
                    </label>
                  </div>

                  {/* Progress Bar - Show when checklist is active or has items */}
                  {(items.length > 0 || showAddItem) && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">
                          {items.length > 0
                            ? `${items.filter((item) => item.completed).length
                            }/${items.length} items`
                            : "0 items"}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">
                          {items.length > 0
                            ? `${Math.round(
                              (items.filter((item) => item.completed).length /
                                items.length) *
                              100
                            )}%`
                            : "0%"}
                        </span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${
                            items.length > 0 && items.every((i) => i.completed)
                              ? "bg-emerald-500"
                              : "bg-blue-600 dark:bg-blue-500"
                          }`}
                          style={{
                            width: `${items.length > 0
                              ? Math.round(
                                (items.filter((item) => item.completed)
                                  .length /
                                  items.length) *
                                100
                              )
                              : 0
                              }%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Items List */}
                  <div className="space-y-1 mb-2.5 max-h-64 overflow-y-auto">
                    {loadingItems ? (
                      <div className="text-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : items.length > 0 ? (
                      items.map((item) => (
                        <div key={item._id}>
                          {editingItemId === item._id ? (
                            /* Edit Mode */
                            <div className="p-2 bg-white dark:bg-slate-800 border border-blue-400 dark:border-blue-500 rounded-lg shadow-sm space-y-2">
                              <input
                                type="text"
                                value={editingItemTitle}
                                onChange={(e) =>
                                  setEditingItemTitle(e.target.value)
                                }
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    handleSaveEditItem(item._id);
                                  } else if (e.key === "Escape") {
                                    handleCancelEditItem();
                                  }
                                }}
                                className="w-full h-8 px-2.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                                autoFocus
                              />
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={handleCancelEditItem}
                                  className="h-7 px-3 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 text-xs font-medium rounded-md transition-colors !cursor-pointer flex items-center justify-center"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSaveEditItem(item._id)}
                                  disabled={!editingItemTitle.trim()}
                                  className="h-7 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed !cursor-pointer flex items-center justify-center"
                                >
                                  Save
                                </button>
                                
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <div
                              onClick={() =>
                                handleToggleItem(item._id, item.completed)
                              }
                              className="flex items-center space-x-2.5 h-8 px-2.5 rounded-lg border border-transparent hover:border-gray-200 dark:hover:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors cursor-pointer group"
                            >
                              <div
                                className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${item.completed
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : "border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800"
                                  }`}
                              >
                                {item.completed && (
                                  <CheckSquare className="w-3 h-3" />
                                )}
                              </div>
                              <div
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditItem(item);
                                }}
                                className={`flex-1 text-left text-xs ${item.completed
                                  ? "line-through text-gray-400 dark:text-slate-500"
                                  : "text-gray-700 dark:text-slate-200"
                                  }`}
                              >
                                {item.title}
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEditItem(item);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all !cursor-pointer"
                                  title="Edit item"
                                >
                                  <Edit className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteItem(item._id);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all !cursor-pointer"
                                  title="Delete item"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-400 dark:text-slate-500 italic text-xs py-1">
                        No checklist items yet
                      </p>
                    )}
                  </div>

                  {/* Add Item Box */}
                  {showAddItem ? (
                    <div className="bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 rounded-lg p-2.5 space-y-2 shadow-sm">
                      <input
                        type="text"
                        value={newItemTitle}
                        onChange={(e) => setNewItemTitle(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleAddItem();
                          } else if (e.key === "Escape") {
                            setShowAddItem(false);
                            setNewItemTitle("");
                          }
                        }}
                        placeholder="Add an item..."
                        className="w-full h-8 px-2.5 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs placeholder:text-gray-400"
                        autoFocus
                      />
                      <div className="flex items-center space-x-2 justify-end">
                        <button
                          onClick={() => {
                            setShowAddItem(false);
                            setNewItemTitle("");
                          }}
                          className="h-7 px-3 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-300 text-xs font-medium rounded-md transition-colors !cursor-pointer flex items-center justify-center"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddItem}
                          disabled={!newItemTitle.trim()}
                          className="h-7 px-3.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed !cursor-pointer flex items-center justify-center"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddItem(true)}
                      className="w-full h-8 border-2 border-dashed border-gray-300 dark:border-slate-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 flex items-center justify-center space-x-2 !cursor-pointer"
                    >
                      <Plus className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      <span className="text-xs font-medium text-gray-600 dark:text-slate-400">
                        Add Checklist
                      </span>
                    </button>
                  )}
                </div>

                {/* Images */}
                {getImageAttachments().length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Images ({getImageAttachments().length})
                    </label>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mb-4">
                      {getImageAttachments().map((attachment, idx) => (
                        <div
                          key={attachment._id || attachment.id}
                          className="relative group cursor-pointer"
                          onClick={() => {
                            setSelectedImage(attachment);
                            setSelectedImageIndex(idx);
                            setShowImageModal(true);
                          }}
                        >
                          <SafeImage
                            src={attachment.url}
                            alt={
                              attachment.originalName ||
                              attachment.filename ||
                              attachment.name
                            }
                            className="w-full h-28 object-cover rounded-lg border border-gray-200 dark:border-slate-700 hover:border-blue-300 transition-colors duration-200"
                            fallback="note"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                            <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAttachmentToDelete({
                                id: attachment._id || attachment.id,
                                closeImageModal: false,
                              });
                              setShowDeleteAttachmentConfirm(true);
                            }}
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-all duration-200"
                            title="Delete image"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Time Tracking - Trello Style */}
                {features.timeTracking && (
                  <TimeTrackerWidget
                    card={card}
                    onCardUpdated={onCardUpdated}
                    isArchived={isArchived}
                    socket={socket}
                    sharedTimer={activeTimer}
                    activeTimers={activeTimers}
                    onTimerStart={onTimerStart}
                    onTimerStop={onTimerStop}
                  />
                )}

                {/* Activity Section with Jira Tabs (All | Comments | History | Work log) */}
                {(() => {
                  const allComments = card.comments || [];
                  const userComments = allComments.filter((c) => !isLegacySystemComment(c));

                  // Gather History / System Activity logs
                  const historyLogs = (() => {

                    // Helper to resolve status/column ID to human-readable name
                    const resolveStatusName = (token) => {
                      if (!token) return "";
                      const trimmed = token.trim();
                      const foundCol = (columns || []).find(
                        (c) =>
                          c.status === trimmed ||
                          c._id === trimmed ||
                          (c.name && c.name.toLowerCase() === trimmed.toLowerCase())
                      );
                      if (foundCol?.name && !foundCol.name.startsWith("custom_")) {
                        return foundCol.name;
                      }
                      const foundOpt = allStatusOptions.find(
                        (s) =>
                          s.value === trimmed ||
                          (s.label && s.label.toLowerCase() === trimmed.toLowerCase())
                      );
                      if (foundOpt?.label && !foundOpt.label.startsWith("custom_")) {
                        return foundOpt.label;
                      }
                      const standardMap = {
                        todo: "To-Do",
                        "in-progress": "In Progress",
                        review: "Review",
                        done: "Done",
                        archive: "Archive",
                      };
                      if (standardMap[trimmed.toLowerCase()]) {
                        return standardMap[trimmed.toLowerCase()];
                      }
                      if (
                        trimmed.startsWith("custom_") ||
                        /^[0-9a-fA-F]{24}$/.test(trimmed)
                      ) {
                        return "";
                      }
                      return trimmed
                        .replace(/[-_]/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase());
                    };

                    const isGenericNoise = (text) => {
                      if (!text) return true;
                      const t = text.trim().toLowerCase();
                      return (
                        t === "card was updated" ||
                        t === "added a comment" ||
                        t === "card was restored"
                      );
                    };

                    const getSemanticKey = (text, userIdStr, time) => {
                      const t = text.toLowerCase();
                      const minuteBucket = Math.floor(time / 60000); // 1-minute bucket
                      if (t.includes("archived this card") || t === "card was archived") {
                        return `archive-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("created this card in") || t.startsWith("card created in")) {
                        return `create-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("restored this card") || t.startsWith("card was restored")) {
                        return `restore-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("assigned") || t === "user assigned to card") {
                        return `assign-${userIdStr}-${minuteBucket}`;
                      }
                      if (
                        t.includes("removed from assignees") ||
                        (t.includes("removed") && t.includes("from this card")) ||
                        t === "user unassigned from card"
                      ) {
                        return `unassign-${userIdStr}-${minuteBucket}`;
                      }
                      if (
                        t.includes("marked this card as complete") ||
                        t.includes("completed this to-do") ||
                        t === "card marked as complete"
                      ) {
                        return `complete-${userIdStr}-${minuteBucket}`;
                      }
                      if (
                        t.includes("marked this card as incomplete") ||
                        t.includes("re-opened this to-do") ||
                        t === "card marked as incomplete"
                      ) {
                        return `incomplete-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("moved this card from")) {
                        return `status-${userIdStr}-${t}-${minuteBucket}`;
                      }
                      if (t.includes("description")) {
                        return `desc-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("renamed the card") || t.includes("title")) {
                        return `title-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("due date")) {
                        return `duedate-${userIdStr}-${minuteBucket}`;
                      }
                      if (t.includes("priority")) {
                        return `priority-${userIdStr}-${minuteBucket}`;
                      }
                      if (
                        t.includes("uploaded an image") ||
                        t.includes("uploaded an attachment") ||
                        t.includes("uploaded a file") ||
                        t.includes("added attachment") ||
                        t.startsWith("uploaded ") ||
                        t.includes("file(s)") ||
                        t.includes("image(s)")
                      ) {
                        return `upload-${userIdStr}-${minuteBucket}`;
                      }
                      return `${t}-${userIdStr}-${minuteBucket}`;
                    };

                    const isDetailedLog = (text) => {
                      if (!text) return false;
                      const t = text.trim();
                      return (
                        !t.startsWith("Card was ") &&
                        !t.startsWith("Card created ") &&
                        !t.startsWith("Card marked ") &&
                        !t.startsWith("User assigned ") &&
                        !t.startsWith("User unassigned ") &&
                        !/^Uploaded \d+ (file|image)\(s\)$/i.test(t) &&
                        !/^Uploaded (files?|images?)$/i.test(t) &&
                        t !== "Added a comment"
                      );
                    };

                    const itemMap = new Map();

                    // 1. Structured entries from card.activityLog
                    if (card.activityLog && Array.isArray(card.activityLog)) {
                      card.activityLog.forEach((item, index) => {
                        let details = (item.details || "").trim();
                        if (
                          !details ||
                          isGenericNoise(details) ||
                          item.action === "commented" ||
                          item.action === "comment_updated" ||
                          item.action === "comment_deleted" ||
                          item.action === "reordered" ||
                          item.action === "reorder" ||
                          item.action?.startsWith("bookmark") ||
                          details.toLowerCase().includes("card order changed") ||
                          details.toLowerCase().includes("bookmarked")
                        ) {
                          return;
                        }

                        // Parse and clean status change logs (prevent raw custom_ IDs)
                        const statusChangeMatch = details.match(
                          /(?:status changed from|moved this card from|changed status from)\s+(.*?)\s+to\s+(.*?)(?:\s+\(bulk move\))?$/i
                        );
                        if (statusChangeMatch) {
                          const rawFrom = statusChangeMatch[1].trim();
                          const rawTo = statusChangeMatch[2].trim();
                          if (rawFrom.toLowerCase() === rawTo.toLowerCase()) {
                            // Filter out no-op changes
                            return;
                          }
                          const fromLabel = resolveStatusName(rawFrom);
                          const toLabel = resolveStatusName(rawTo);
                          if (
                            !fromLabel ||
                            !toLabel ||
                            fromLabel.toLowerCase() === toLabel.toLowerCase()
                          ) {
                            return;
                          }
                          const userName =
                            item.user?.name ||
                            (typeof item.user === "string" ? "User" : "User");
                          details = `${userName} moved this card from ${fromLabel} to ${toLabel}`;
                        } else if (details.includes("custom_")) {
                          details = details.replace(
                            /custom_\d+_[a-z0-9]+/gi,
                            (m) => resolveStatusName(m) || "Column"
                          );
                        }

                        // If details has "Removed/Added label <hex_id>", resolve to label name if known
                        if (item.action === "label_removed" || item.action === "label_added") {
                          const hexMatch = details.match(/^(Added|Removed) label ([0-9a-fA-F]{24})$/i);
                          if (hexMatch) {
                            const actionPrefix = hexMatch[1];
                            const targetId = hexMatch[2];
                            const allKnownLabels = [...(availableLabels || []), ...(card.labels || [])];
                            const found = allKnownLabels.find(
                              (l) => (l._id || l.id)?.toString() === targetId
                            );
                            if (found && found.name) {
                              details = `${actionPrefix} label ${found.name}`;
                            }
                          }
                        }

                        const time = new Date(
                          item.timestamp || item.createdAt || Date.now()
                        ).getTime();
                        const userIdStr =
                          item.user?._id || item.user?.id || String(item.user || "");
                        const sKey = getSemanticKey(details, userIdStr, time);

                        const logObj = {
                          id: item._id || `act-${index}`,
                          user: item.user,
                          action: item.action,
                          details: details,
                          text: details || item.action,
                          timestamp: item.timestamp || item.createdAt || new Date(),
                          type: "activity",
                          isDetailed: isDetailedLog(details),
                        };

                        if (itemMap.has(sKey)) {
                          const existing = itemMap.get(sKey);
                          // If current is detailed and existing is not, replace it
                          if (logObj.isDetailed && !existing.isDetailed) {
                            itemMap.set(sKey, logObj);
                          }
                        } else {
                          itemMap.set(sKey, logObj);
                        }
                      });
                    }

                    // 2. Legacy system comments from card.comments
                    allComments.forEach((c) => {
                      if (isLegacySystemComment(c)) {
                        const time = new Date(
                          c.timestamp || c.createdAt || Date.now()
                        ).getTime();
                        let textSnippet = (c.text || "")
                          .replace(/<[^>]*>/g, "")
                          .trim();

                        if (
                          !textSnippet ||
                          isGenericNoise(textSnippet) ||
                          c.action === "reordered" ||
                          c.action === "reorder" ||
                          c.action?.startsWith("bookmark") ||
                          textSnippet.toLowerCase().includes("card order changed") ||
                          textSnippet.toLowerCase().includes("bookmarked")
                        ) {
                          return;
                        }

                        const statusChangeMatch = textSnippet.match(
                          /(?:status changed from|moved this card from|changed status from)\s+(.*?)\s+to\s+(.*?)(?:\s+\(bulk move\))?$/i
                        );
                        if (statusChangeMatch) {
                          const rawFrom = statusChangeMatch[1].trim();
                          const rawTo = statusChangeMatch[2].trim();
                          if (rawFrom.toLowerCase() === rawTo.toLowerCase()) {
                            return;
                          }
                          const fromLabel = resolveStatusName(rawFrom);
                          const toLabel = resolveStatusName(rawTo);
                          if (
                            !fromLabel ||
                            !toLabel ||
                            fromLabel.toLowerCase() === toLabel.toLowerCase()
                          ) {
                            return;
                          }
                          const userName = c.user?.name || "User";
                          textSnippet = `${userName} moved this card from ${fromLabel} to ${toLabel}`;
                        } else if (textSnippet.includes("custom_")) {
                          textSnippet = textSnippet.replace(
                            /custom_\d+_[a-z0-9]+/gi,
                            (m) => resolveStatusName(m) || "Column"
                          );
                        }

                        const userIdStr =
                          c.user?._id || c.user?.id || String(c.user || "");
                        const sKey = getSemanticKey(textSnippet, userIdStr, time);

                        const logObj = {
                          id: c._id || c.id,
                          user: c.user,
                          action: "system_comment",
                          details: textSnippet,
                          text: textSnippet,
                          timestamp: c.timestamp || c.createdAt || new Date(),
                          type: "system_comment",
                          isDetailed: isDetailedLog(textSnippet),
                        };

                        if (itemMap.has(sKey)) {
                          const existing = itemMap.get(sKey);
                          if (logObj.isDetailed && !existing.isDetailed) {
                            itemMap.set(sKey, logObj);
                          }
                        } else {
                          itemMap.set(sKey, logObj);
                        }
                      }
                    });

                    const list = Array.from(itemMap.values());

                    // Sort descending (most recent first)
                    list.sort(
                      (a, b) =>
                        new Date(b.timestamp || b.createdAt) -
                        new Date(a.timestamp || a.createdAt)
                    );
                    return list;
                  })();

                  // Build a map of valid user comment IDs
                  const userCommentIdMap = new Map();
                  userComments.forEach((c) => {
                    const id = (c._id || c.id)?.toString();
                    if (id) userCommentIdMap.set(id, c);
                  });

                  // Replies map for user comments (only for valid parent comments)
                  const repliesByParentId = {};
                  userComments.forEach((c) => {
                    const parentIdStr = c.parentId ? c.parentId.toString() : null;
                    if (parentIdStr && userCommentIdMap.has(parentIdStr)) {
                      if (!repliesByParentId[parentIdStr]) repliesByParentId[parentIdStr] = [];
                      repliesByParentId[parentIdStr].push(c);
                    }
                  });
                  // Sort replies in chronological order
                  Object.keys(repliesByParentId).forEach((pId) => {
                    repliesByParentId[pId].sort(
                      (a, b) =>
                        new Date(a.timestamp || a.createdAt) -
                        new Date(b.timestamp || b.createdAt)
                    );
                  });

                  // Root user comments (no parentId or parent does not exist in userComments)
                  const rootComments = userComments
                    .filter((c) => {
                      const parentIdStr = c.parentId ? c.parentId.toString() : null;
                      return !parentIdStr || !userCommentIdMap.has(parentIdStr);
                    })
                    .sort((a, b) => {
                      const aTime = a.updatedAt || a.timestamp || a.createdAt;
                      const bTime = b.updatedAt || b.timestamp || b.createdAt;
                      return new Date(bTime) - new Date(aTime);
                    });

                  // Combined feed for "All" tab (Root Comments + History Logs sorted chronologically)
                  const allFeedItems = [
                    ...rootComments.map((c) => ({
                      ...c,
                      feedType: "comment",
                      itemTime: new Date(c.updatedAt || c.timestamp || c.createdAt),
                    })),
                    ...historyLogs.map((h) => ({
                      ...h,
                      feedType: "history",
                      itemTime: new Date(h.timestamp || h.createdAt),
                    })),
                  ].sort((a, b) => b.itemTime - a.itemTime);

                  const renderCommentNode = (
                    comment,
                    isReply = false,
                    rootParentId = null
                  ) => {
                    const commentId = comment._id || comment.id;
                    const isAuthor =
                      comment.user?._id === user?._id ||
                      comment.user?._id === user?.id ||
                      (typeof comment.user === "string" &&
                        comment.user === (user?._id || user?.id));
                    const isCardOwner =
                      currentProject?.owner === user?._id ||
                      currentProject?.owner?._id === user?._id;
                    const canDelete = isAuthor && !isLegacySystemComment(comment);
                    const canEdit = isAuthor && !isLegacySystemComment(comment);
                    const isEditingThisComment = editingComment === commentId;
                    const isReplyingToThisComment =
                      replyingToCommentId === commentId;
                    const childReplies = repliesByParentId[commentId] || [];

                    const date = new Date(comment.timestamp || comment.createdAt);
                    const day = String(date.getDate()).padStart(2, "0");
                    const month = date.toLocaleString("en-US", { month: "short" });
                    const year = date.getFullYear();
                    const time = date.toLocaleString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    const formattedDate = `${day} ${month}, ${year} at ${time}`;

                    return (
                      <div
                        key={commentId}
                        className={`${
                          isReply
                            ? "bg-white dark:bg-slate-800/90 border border-gray-200 dark:border-slate-700/80 rounded-lg p-3 shadow-sm hover:shadow transition-shadow"
                            : "bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                        } group`}
                      >
                        {/* Comment Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2.5">
                            <Avatar
                              user={comment.user}
                              size={isReply ? "xs" : "sm"}
                              fallback={
                                comment.user?.name
                                  ? comment.user.name.charAt(0).toUpperCase()
                                  : "U"
                              }
                            />
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold text-gray-900 dark:text-white text-sm">
                                {comment.user?.name ||
                                  (comment.user &&
                                  typeof comment.user === "string"
                                    ? "Loading..."
                                    : "Unknown User")}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-slate-400">
                                {formattedDate}
                                {comment.updatedAt &&
                                  comment.updatedAt !== comment.timestamp && (
                                    <span className="ml-1 text-gray-400 dark:text-slate-500 font-normal">
                                      (edited)
                                    </span>
                                  )}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Comment Content / Edit Mode */}
                        {isEditingThisComment ? (
                          <div
                            className="comment-editor rounded-xl border border-gray-300 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-sm mt-2"
                            onPasteCapture={handleComposerPaste(handleEditFileSelect)}
                          >
                            <SimpleCommentEditor
                              value={editCommentText || ""}
                              onChange={(content) => setEditCommentText(content)}
                              placeholder="Edit your comment..."
                              projectMembers={currentProject?.members || []}
                              currentUser={user}
                              cardMembers={getAssignees()}
                            />

                            {editExistingAttachmentHtml && (
                              <div className="px-3 pt-2 pb-1 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
                                <p className="text-[11px] font-medium text-gray-400 dark:text-slate-500 mb-1">
                                  Existing attachment(s) — kept as-is
                                </p>
                                <div
                                  className="[&_img]:inline-block [&_img]:w-16 [&_img]:h-14 [&_img]:object-cover [&_img]:rounded-lg [&_img]:border [&_img]:border-gray-200 [&_img]:dark:border-slate-700 [&_img]:mr-1.5 [&_img]:opacity-80 [&_a]:inline-flex [&_a]:items-center [&_a]:px-2 [&_a]:py-1 [&_a]:rounded-lg [&_a]:bg-gray-100 [&_a]:dark:bg-slate-800 [&_a]:text-gray-500 [&_a]:dark:text-slate-400 [&_a]:text-xs [&_a]:pointer-events-none"
                                  dangerouslySetInnerHTML={{
                                    __html: editExistingAttachmentHtml,
                                  }}
                                />
                              </div>
                            )}

                            {editPendingFiles.length > 0 && (
                              <div className="flex flex-wrap gap-2 px-3 pt-2 pb-1 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
                                {editPendingFiles.map((file, idx) => (
                                  <div
                                    key={`${file.name}-${idx}`}
                                    className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs text-gray-700 dark:text-slate-300"
                                  >
                                    <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <span className="truncate max-w-[140px]">
                                      {file.name}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => removeEditPendingFile(idx)}
                                      className="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Footer Action Bar */}
                            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800/60 border-t border-gray-200 dark:border-slate-700/60">
                              <input
                                ref={editFileInputRef}
                                type="file"
                                multiple
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                                className="hidden"
                                onChange={(e) => {
                                  handleEditFileSelect(e.target.files);
                                  e.target.value = "";
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => editFileInputRef.current?.click()}
                                disabled={isUploadingEditFiles}
                                className="h-8 px-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-xs font-medium text-gray-700 dark:text-slate-200 flex items-center gap-1.5 transition-colors disabled:opacity-50 !cursor-pointer"
                                title="Attach a file or image"
                              >
                                <Paperclip className="w-3.5 h-3.5 text-gray-500 dark:text-slate-400" />
                                <span>
                                  {isUploadingEditFiles ? "Uploading…" : "Attach"}
                                </span>
                              </button>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={handleCancelEditComment}
                                  className="h-8 px-3 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-colors !cursor-pointer flex items-center justify-center"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleSaveEditComment}
                                  disabled={isUploadingEditFiles}
                                  className="h-8 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm !cursor-pointer"
                                >
                                  <Send className="w-3 h-3" />
                                  <span>Save</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="text-sm md:text-[15px] leading-relaxed text-gray-800 dark:text-slate-100 prose prose-sm dark:prose-invert max-w-none [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1 [&_p]:my-1.5 [&_p]:leading-relaxed
                            [&_img]:inline-block [&_img]:w-24 [&_img]:h-20 [&_img]:object-cover [&_img]:rounded-lg [&_img]:border [&_img]:border-gray-200 [&_img]:dark:border-slate-700 [&_img]:shadow-sm [&_img]:mr-1.5 [&_img]:mt-2 [&_img]:mb-1 [&_img]:cursor-pointer [&_img]:transition-all [&_img]:duration-200 [&_img]:hover:shadow-md [&_img]:hover:border-blue-300
                            [&_a]:inline-flex [&_a]:items-center [&_a]:gap-1.5 [&_a]:px-2.5 [&_a]:py-1 [&_a]:mt-1 [&_a]:rounded-lg [&_a]:bg-blue-50 [&_a]:dark:bg-blue-900/20 [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:font-medium [&_a]:no-underline [&_a]:hover:bg-blue-100 [&_a]:dark:hover:bg-blue-900/40 [&_a]:transition-colors [&_a]:duration-200"
                            onClick={(e) => {
                              if (e.target.tagName !== "IMG") return;
                              const clickedSrc = e.target.getAttribute("src");
                              const images = getImageAttachments();
                              const idx = images.findIndex((att) => {
                                const resolvedUrl = att.url?.startsWith("http")
                                  ? att.url
                                  : `${API_URL}${att.url}`;
                                return resolvedUrl === clickedSrc;
                              });
                              if (idx !== -1) {
                                setSelectedImage(images[idx]);
                                setSelectedImageIndex(idx);
                                setShowImageModal(true);
                              }
                            }}
                            dangerouslySetInnerHTML={{
                              __html:
                                renderCommentWithMentions(comment.text)?.replace(
                                  /\n/g,
                                  "<br />"
                                ) || "<p><br></p>",
                            }}
                          />
                        )}

                        {/* Jira-Style Action Bar (Reply, Edit, Delete) */}
                        {!isEditingThisComment && !isLegacySystemComment(comment) && (
                          <div className="flex items-center gap-2 mt-2 pt-1 border-t border-gray-100 dark:border-slate-800/60">
                            <button
                              type="button"
                              onClick={() => handleStartReply(comment)}
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-slate-800 !cursor-pointer"
                              title="Reply to this comment"
                            >
                              <CornerUpLeft className="w-3.5 h-3.5" />
                              <span>Reply</span>
                            </button>

                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => handleStartEditComment(comment)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-slate-800 !cursor-pointer"
                                title="Edit this comment"
                              >
                                <Edit className="w-3.5 h-3.5" />
                                <span>Edit</span>
                              </button>
                            )}

                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCommentToDelete(comment);
                                  setShowDeleteCommentConfirm(true);
                                }}
                                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors py-1 px-2 rounded hover:bg-gray-100 dark:hover:bg-slate-800 !cursor-pointer"
                                title="Delete this comment"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Delete</span>
                              </button>
                            )}

                            {!isReply && childReplies.length > 0 && (
                              <span className="ml-auto text-[11px] font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
                                {childReplies.length}{" "}
                                {childReplies.length === 1 ? "reply" : "replies"}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Nested Replies List (Indented under parent) */}
                        {!isReply && childReplies.length > 0 && (
                          <div className="mt-3 pl-3.5 ml-2 border-l border-gray-200 dark:border-slate-700/80 space-y-2.5">
                            {childReplies.map((reply) =>
                              renderCommentNode(reply, true, commentId)
                            )}
                          </div>
                        )}

                        {/* Inline Reply Composer (Positioned under thread) */}
                        {isReplyingToThisComment && (
                          <div className="mt-3 pl-3.5 ml-2 border-l border-blue-500 dark:border-blue-400 space-y-2">
                            <div className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-slate-300">
                              <Avatar user={user} size="xs" />
                              <span>
                                Replying to{" "}
                                <strong className="text-blue-600 dark:text-blue-400">
                                  {replyToUser?.name || "User"}
                                </strong>
                              </span>
                            </div>

                            <div
                              className="comment-editor rounded-xl border border-gray-300 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-sm"
                              onPasteCapture={handleComposerPaste(handleReplyFileSelect)}
                            >
                              <SimpleCommentEditor
                                value={replyCommentText || ""}
                                onChange={(content) => setReplyCommentText(content)}
                                onMentionSelect={(mention) =>
                                  setReplyMentions((prev) => [...prev, mention])
                                }
                                placeholder={`Reply to ${
                                  replyToUser?.name || "user"
                                }...`}
                                projectMembers={currentProject?.members || []}
                                currentUser={user}
                                cardMembers={getAssignees()}
                              />

                              {replyPendingFiles.length > 0 && (
                                <div className="flex flex-wrap gap-2 px-3 pt-2 pb-1 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
                                  {replyPendingFiles.map((file, idx) => (
                                    <div
                                      key={`${file.name}-${idx}`}
                                      className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs text-gray-700 dark:text-slate-300"
                                    >
                                      <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                      <span className="truncate max-w-[140px]">
                                        {file.name}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeReplyPendingFile(idx)
                                        }
                                        className="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Action Bar */}
                              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800/60 border-t border-gray-200 dark:border-slate-700/60">
                                <input
                                  ref={replyFileInputRef}
                                  type="file"
                                  multiple
                                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                                  className="hidden"
                                  onChange={(e) => {
                                    handleReplyFileSelect(e.target.files);
                                    e.target.value = "";
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => replyFileInputRef.current?.click()}
                                  disabled={isUploadingReplyFiles}
                                  className="h-8 px-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-xs font-medium text-gray-700 dark:text-slate-200 flex items-center gap-1.5 transition-colors disabled:opacity-50 !cursor-pointer"
                                  title="Attach a file"
                                >
                                  <Paperclip className="w-3.5 h-3.5 text-gray-500 dark:text-slate-400" />
                                  <span>Attach</span>
                                </button>

                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={handleCancelReply}
                                    className="h-8 px-3 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-colors !cursor-pointer flex items-center justify-center"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddReply(
                                        rootParentId ||
                                          comment.parentId ||
                                          commentId
                                      )
                                    }
                                    disabled={isUploadingReplyFiles}
                                    className="h-8 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm !cursor-pointer"
                                  >
                                    <Send className="w-3 h-3" />
                                    <span>Save</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  };

                  const renderHistoryNode = (log) => {
                    const logDate = new Date(log.timestamp || log.createdAt);
                    const day = String(logDate.getDate()).padStart(2, "0");
                    const month = logDate.toLocaleString("en-US", {
                      month: "short",
                    });
                    const year = logDate.getFullYear();
                    const time = logDate.toLocaleString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    const formattedDate = `${day} ${month}, ${year} at ${time}`;

                    return (
                      <div
                        key={log.id}
                        className="flex items-start gap-3 p-3 bg-gray-50/90 dark:bg-slate-900/50 border border-gray-200/90 dark:border-slate-800 rounded-xl transition-all hover:bg-gray-50 dark:hover:bg-slate-900/70"
                      >
                        <Avatar
                          user={log.user}
                          size="xs"
                          fallback={
                            log.user?.name
                              ? log.user.name.charAt(0).toUpperCase()
                              : "S"
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900 dark:text-white text-sm">
                              {log.user?.name ||
                                (typeof log.user === "string"
                                  ? "System"
                                  : "System")}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-slate-400">
                              {formattedDate}
                            </span>
                          </div>
                          <div
                            className="text-[13.5px] leading-relaxed text-gray-700 dark:text-slate-300 [&_p]:my-0 [&_strong]:font-semibold [&_strong]:text-gray-900 [&_strong]:dark:text-white [&_span]:inline-block [&_span]:text-[13px]"
                            dangerouslySetInnerHTML={{
                              __html:
                                log.text || log.details || "Activity recorded",
                            }}
                          />
                        </div>
                      </div>
                    );
                  };

                  return (
                    <div className="bg-white dark:bg-slate-800 pb-3 transition-colors duration-200">
                      {/* Activity Title & Jira Tabs: Comments | Work log */}
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2.5">
                          Activity
                        </h3>

                        <div className="inline-flex items-center p-0.5 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 shadow-xs">
                          {[
                            {
                              key: "comments",
                              label: "Comments",
                              count: userComments.length,
                            },
                            {
                              key: "work_log",
                              label: "Work log",
                              count: historyLogs.length,
                            },
                          ].map((tab) => {
                            const isActive = activeActivityTab === tab.key;
                            return (
                              <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveActivityTab(tab.key)}
                                className={`h-7 px-2.5 text-xs rounded-md transition-all !cursor-pointer flex items-center justify-center gap-1.5 ${
                                  isActive
                                    ? "border border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-500 font-medium"
                                    : "border border-transparent text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200"
                                }`}
                              >
                                <span>{tab.label}</span>
                                <span
                                  className={`text-[11px] px-1.5 py-0.5 min-w-[18px] text-center rounded-full leading-none font-medium ${
                                    isActive
                                      ? "bg-blue-200/70 text-blue-700 dark:bg-blue-900/70 dark:text-blue-200 font-semibold"
                                      : "bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-400"
                                  }`}
                                >
                                  {tab.count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Tab 1: Comments View */}
                      {activeActivityTab === "comments" && (
                        <div>
                          {/* Add Comment Composer */}
                          <div className="mb-4">
                            <div
                              className="comment-editor rounded-xl border border-gray-300 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-sm transition-all"
                              onPasteCapture={handleComposerPaste(handleCommentFileSelect)}
                            >
                              <SimpleCommentEditor
                                value={commentText || ""}
                                onChange={(content) => {
                                  setCommentText(content);
                                }}
                                onMentionSelect={(mention) => {
                                  setMentions((prev) => [...prev, mention]);
                                }}
                                onSend={(content) => {
                                  setCommentText(content);
                                  handleAddComment();
                                }}
                                placeholder="Add a comment... (use @ to mention someone)"
                                projectMembers={currentProject?.members || []}
                                currentUser={user}
                                cardMembers={getAssignees()}
                              />

                              {/* Pending attachments */}
                              {pendingCommentFiles.length > 0 && (
                                <div className="flex flex-wrap gap-2 px-3 pt-2 pb-1 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800">
                                  {pendingCommentFiles.map((file, idx) => (
                                    <div
                                      key={`${file.name}-${idx}`}
                                      className="flex items-center gap-1.5 pl-2 pr-1 py-1 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs text-gray-700 dark:text-slate-300"
                                    >
                                      <Paperclip className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                      <span className="truncate max-w-[140px]">{file.name}</span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removePendingCommentFile(idx)
                                        }
                                        className="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                                        title="Remove attachment"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Footer Action Bar */}
                              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800/60 border-t border-gray-200 dark:border-slate-700/60">
                                <input
                                  ref={commentFileInputRef}
                                  type="file"
                                  multiple
                                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                                  className="hidden"
                                  onChange={(e) => {
                                    handleCommentFileSelect(e.target.files);
                                    e.target.value = "";
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    commentFileInputRef.current?.click()
                                  }
                                  disabled={isUploadingCommentFiles}
                                  className="h-8 px-3 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 text-xs font-medium text-gray-700 dark:text-slate-200 flex items-center gap-1.5 transition-colors disabled:opacity-50 !cursor-pointer"
                                  title="Attach a file or image to this comment"
                                >
                                  <Paperclip className="w-3.5 h-3.5 text-gray-500 dark:text-slate-400" />
                                  <span>
                                    {isUploadingCommentFiles
                                      ? "Uploading…"
                                      : "Attach"}
                                  </span>
                                </button>

                                <div className="flex items-center gap-2">
                                  {(commentText.trim() ||
                                    pendingCommentFiles.length > 0) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setCommentText("");
                                        setPendingCommentFiles([]);
                                      }}
                                      className="h-8 px-3 text-xs font-medium text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-colors !cursor-pointer flex items-center justify-center"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={handleAddComment}
                                    disabled={isUploadingCommentFiles}
                                    className="h-8 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm !cursor-pointer"
                                  >
                                    <Send className="w-3 h-3" />
                                    <span>Save</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Threaded Comments List */}
                          <div className="space-y-3 mb-6 pr-2">
                            {rootComments.length > 0 ? (
                              rootComments.map((rootComment) =>
                                renderCommentNode(rootComment, false, null)
                              )
                            ) : (
                              <p className="text-xs text-gray-400 dark:text-slate-500 italic py-2">
                                No comments yet. Start the conversation above!
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Tab 2: Work log View (All work/activity logs except comments) */}
                      {activeActivityTab === "work_log" && (
                        <div className="space-y-2.5 mb-6 pr-2">
                          {historyLogs.length > 0 ? (
                            historyLogs.map(renderHistoryNode)
                          ) : (
                            <p className="text-xs text-gray-400 dark:text-slate-500 italic py-3">
                              No work log entries recorded for this card yet.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-3 max-h-[85vh] overflow-y-auto px-2">
                <div className="status-date bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 transition-colors duration-200 group">
                  {/* Project - Allow moving card to another project */}
                  {allProjectOptions.length > 0 && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        <Redo2 className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                        Move to Project
                      </label>
                      <CustomProjectDropdown
                        value={card.project?._id || card.project}
                        currentProjectLabel={currentProjectLabel}
                        options={allProjectOptions}
                        onChange={handleProjectChange}
                        disabled={isArchived || loadingProjectChange}
                        isLoading={loadingProjectChange}
                      />
                    </div>
                  )}
                  {/* Status */}
                  <div className="mt-2">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      <CornerDownRight className="w-4 h-4 text-gray-600 dark:text-slate-400" />
                      Status
                      {isArchived && (
                        <span className="text-xs text-gray-500 dark:text-slate-500 ml-2 font-normal">
                          (Disabled for archived cards)
                        </span>
                      )}
                      {selectedProjectForMove && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 ml-2 font-normal">
                          (Showing statuses from {movedProjectName})
                        </span>
                      )}
                    </label>
                    <CustomStatusDropdown
                      value={selectedProjectForMove ? "" : card.status}
                      options={allStatusOptions}
                      onChange={handleStatusChange}
                      disabled={isArchived}
                      isLoading={loadingColumns}
                    />
                  </div>

                  {/* Due Date */}
                  <div>
                    <div className="flex items-center justify-between mb-2 mt-2">
                      <div className="flex items-center gap-2">
                        <Clock4 className="w-4 h-4 text-gray-700 dark:text-slate-300" />

                        <label className="text-sm font-semibold text-gray-700 dark:text-slate-300 flex items-center gap-1">
                          Due Date
                          {isArchived && (
                            <span className="text-xs text-gray-500 dark:text-slate-500 font-normal">
                              (Disabled for archived cards)
                            </span>
                          )}
                        </label>
                      </div>

                      {autoSaving && (
                        <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                          <svg
                            className="animate-spin h-3 w-3 mr-1"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Saving...
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => !isArchived && setShowDatePicker(!showDatePicker)}
                        className={`w-full h-8 px-3 pr-10 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs bg-white dark:bg-slate-900 text-black dark:text-white text-left flex items-center transition-all duration-200 ${isArchived
                          ? "bg-gray-100 dark:bg-slate-800 cursor-not-allowed text-gray-500 dark:text-slate-500"
                          : "hover:border-blue-400 dark:hover:border-blue-500"
                          }`}
                        disabled={autoSaving || isArchived}
                      >
                        {formData.dueDate
                          ? new Date(formData.dueDate).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                          : "No due date"}
                      </button>
                      {formData.dueDate && !isArchived ? (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, dueDate: "" });
                            setShowDatePicker(false);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          title="Clear due date"
                          disabled={autoSaving}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      ) : !isArchived ? (
                        <Calendar className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500 pointer-events-none" />
                      ) : null}
                      {showDatePicker && !isArchived && (
                        <CustomDatePicker
                          value={formData.dueDate}
                          onChange={(newDate) => setFormData({ ...formData, dueDate: newDate })}
                          onClose={() => setShowDatePicker(false)}
                          isArchived={isArchived}
                        />
                      )}
                    </div>
                  </div>

                </div>

                {/* Related Cards Section */}
                <div
                  ref={relatedCardsContainerRef}
                  className="related-cards bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 transition-colors duration-200 group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                      <label className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                        Related Cards {card.relatedCards && card.relatedCards.length > 0 ? `(${card.relatedCards.length})` : ""}
                      </label>
                    </div>
                    {!isArchived && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowRelatedCardsSearch((prev) => !prev);
                          if (!showRelatedCardsSearch) {
                            setTimeout(() => {
                              relatedSearchInputRef.current?.focus();
                            }, 50);
                          }
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline dark:hover:text-blue-300 text-sm font-semibold cursor-pointer flex items-center gap-1 transition-colors"
                      >
                        <span>+Add</span>
                      </button>
                    )}
                  </div>

                  {/* Search Bar Popover / Inline Box */}
                  {showRelatedCardsSearch && !isArchived && (
                    <div className="relative mb-3">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
                        <input
                          ref={relatedSearchInputRef}
                          type="text"
                          value={relatedSearchQuery}
                          onChange={(e) => setRelatedSearchQuery(e.target.value)}
                          placeholder="Search card #ID or title..."
                          className="w-full h-8 pl-8 pr-7 text-xs border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {relatedSearchQuery && (
                          <button
                            type="button"
                            onClick={() => setRelatedSearchQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* Search Results - AS A FLOATING DROPDOWN */}
                      {relatedSearchQuery.trim().length > 0 && (
                        <div className="absolute left-0 right-0 top-full mt-1.5 p-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl max-h-52 overflow-y-auto space-y-1 custom-scrollbar z-50">
                          {isSearchingRelated ? (
                            <div className="flex items-center justify-center py-3 text-gray-400 text-xs gap-1.5">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Searching cards...</span>
                            </div>
                          ) : relatedSearchResults.length > 0 ? (
                            relatedSearchResults.map((result) => {
                              const isChecked = (card.relatedCards || []).some(
                                (rc) => String(rc._id || rc) === String(result._id)
                              );
                              const isItemLoading = relatedLoadingCardId === result._id;

                              return (
                                <div
                                  key={result._id}
                                  onClick={() => !isItemLoading && handleToggleRelatedCard(result._id)}
                                  className={`h-8 px-2.5 flex items-center justify-between rounded-md cursor-pointer transition-colors text-xs select-none ${
                                    isChecked
                                      ? "bg-blue-50/80 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200"
                                      : "hover:bg-gray-100 dark:hover:bg-slate-700/60 text-gray-700 dark:text-slate-300"
                                  }`}
                                >
                                  <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {}}
                                      className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 dark:border-slate-600 focus:ring-blue-500 cursor-pointer pointer-events-none"
                                    />
                                    <span className="font-semibold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                      #{result.cardNumber}
                                    </span>
                                    <span className="truncate font-medium" title={result.title}>
                                      {result.title}
                                    </span>
                                  </div>
                                  {isItemLoading ? (
                                    <Loader2 className="w-3 h-3 animate-spin text-blue-500 flex-shrink-0" />
                                  ) : (() => {
                                    const label = getCardStatusLabel(result.status, result);
                                    if (!label) return null;
                                    return (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200/70 dark:bg-slate-700 text-gray-600 dark:text-slate-300 flex-shrink-0">
                                        {label}
                                      </span>
                                    );
                                  })()}
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-gray-400 dark:text-slate-500 text-center py-2 text-xs">
                              No matching cards found
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Related Cards List */}
                  {card.relatedCards && card.relatedCards.length > 0 ? (
                    <div className="space-y-1.5">
                      {card.relatedCards.map((rc) => {
                        const rcId = rc._id || rc;
                        const isUnlinking = relatedLoadingCardId === rcId;
                        const rcTitle = rc.title || `Card #${rc.cardNumber || ""}`;

                        return (
                          <div
                            key={rcId}
                            onClick={() => {
                              if (typeof rc === "object" && rc._id) {
                                handleNavigateToCard(rc);
                              }
                            }}
                            className="h-8 px-2.5 flex items-center justify-between rounded-lg bg-white dark:bg-slate-800 border border-gray-200/80 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer group/item shadow-xs"
                            title={`Click to open #${rc.cardNumber || ""} - ${rcTitle}`}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                                #{rc.cardNumber}
                              </span>
                              <span className="text-xs font-medium text-gray-800 dark:text-slate-200 truncate">
                                {rcTitle}
                              </span>
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {(() => {
                                const label = getCardStatusLabel(rc.status, rc);
                                if (!label) return null;
                                return (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
                                    {label}
                                  </span>
                                );
                              })()}
                              {!isArchived && (
                                <button
                                  type="button"
                                  onClick={(e) => handleRemoveRelatedCard(rcId, e)}
                                  disabled={isUnlinking}
                                  className="p-1 text-gray-400 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400 rounded transition-colors opacity-0 group-hover/item:opacity-100"
                                  title="Unlink related card"
                                >
                                  {isUnlinking ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <X className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-slate-400 italic">
                      No related cards linked yet.
                    </p>
                  )}
                </div>

                <div className="priority bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 transition-colors duration-200 group">


                  {/* Priority */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                      <label className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                        Priority
                      </label>
                    </div>

                    <div className="flex space-x-1">
                      {["low", "medium", "high"].map((priority) => (
                        <button
                          key={priority}
                          onClick={() => handleSetPriority(priority)}
                          className={`h-8 px-3 rounded text-xs font-medium flex items-center justify-center ${card.priority === priority
                            ? priority === "high"
                              ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                              : priority === "medium"
                                ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                                : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                            : "bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-slate-600"
                            }`}
                        >
                          {priority.charAt(0).toUpperCase() + priority.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="label relative bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 transition-colors duration-200 group">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                      <label className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                        Labels ({card.labels?.length || 0})
                      </label>
                    </div>

                    {/* Direct Searchable Input Trigger */}
                    <div
                      ref={labelsSectionRef}
                      onClick={() => {
                        setShowLabelsDropdown(true);
                        labelInputRef.current?.focus();
                      }}
                      className={`w-full min-h-[32px] py-1 px-2.5 border rounded-lg bg-white dark:bg-slate-900 flex items-center justify-between gap-1.5 transition-all text-xs cursor-text ${
                        showLabelsDropdown
                          ? "border-blue-500 ring-1 ring-blue-500"
                          : "border-gray-300 dark:border-slate-600 hover:border-blue-400"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                        {card.labels?.map((label) => {
                          const colorConfig = getLabelColor(label.color);

                          return (
                            <span
                              key={label._id || label.id || label.name}
                              className={`inline-flex items-center gap-1 px-2 h-[22px] rounded text-xs font-medium shadow-xs ${colorConfig?.bg || "bg-blue-500"} ${colorConfig?.text || "text-white"}`}
                            >
                              <span className="truncate max-w-[140px] leading-tight">{label.name}</span>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveLabel(label._id || label.id || label);
                                }}
                                className="hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                        <input
                          ref={labelInputRef}
                          type="text"
                          value={labelSearchTerm}
                          onChange={(e) => {
                            setLabelSearchTerm(e.target.value);
                            if (!showLabelsDropdown) setShowLabelsDropdown(true);
                          }}
                          onFocus={() => setShowLabelsDropdown(true)}
                          placeholder={(!card.labels || card.labels.length === 0) ? "Search labels..." : ""}
                          className="bg-transparent outline-none flex-1 min-w-[60px] text-xs text-gray-900 dark:text-slate-100 placeholder:text-gray-400"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowLabelsDropdown((prev) => !prev);
                        }}
                        className="p-0.5 text-gray-500 hover:text-gray-700 dark:hover:text-slate-300 flex-shrink-0 self-center"
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform flex-shrink-0 ${
                            showLabelsDropdown ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <CardLabelsDropdown
                    isOpen={showLabelsDropdown}
                    onClose={() => setShowLabelsDropdown(false)}
                    anchorRef={labelsSectionRef}
                    availableLabels={availableLabels}
                    selectedLabels={card.labels || []}
                    searchTerm={labelSearchTerm}
                    setSearchTerm={setLabelSearchTerm}
                    showCreateLabel={showCreateLabel}
                    setShowCreateLabel={setShowCreateLabel}
                    newLabelName={newLabelName}
                    setNewLabelName={setNewLabelName}
                    newLabelColor={newLabelColor}
                    setNewLabelColor={setNewLabelColor}
                    onToggleLabel={handleToggleLabel}
                    onCreateLabel={handleCreateLabel}
                    onUpdateLabel={handleUpdateLabel}
                  />
                </div>

                <div className="assignees relative bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 transition-colors duration-200 group">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                        <label className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                          Assignee ({assignees.length})
                        </label>
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => {
                          if (isCurrentUserAssigned) {
                            setShowMembersDropdown((prev) => !prev);
                          } else if (user) {
                            handleToggleAssignee(user);
                          } else {
                            setShowMembersDropdown(true);
                          }
                        }}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-xs font-semibold cursor-pointer"
                      >
                        {assigneeButtonLabel}
                      </button>
                    </div>

                    {/* Direct Searchable Input Trigger */}
                    <div
                      ref={assigneeSectionRef}
                      onClick={() => {
                        setShowMembersDropdown(true);
                        memberInputRef.current?.focus();
                      }}
                      className={`w-full h-8 min-h-[32px] px-2.5 border rounded-lg bg-white dark:bg-slate-900 flex items-center justify-between gap-1.5 transition-all text-xs cursor-text ${
                        showMembersDropdown
                          ? "border-blue-500 ring-1 ring-blue-500"
                          : "border-gray-300 dark:border-slate-600 hover:border-blue-400"
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                        {assignees.map((member) => (
                          <span
                            key={member._id || member.id}
                            className="inline-flex items-center gap-1.5 pl-0.5 pr-1.5 h-[22px] rounded text-xs font-medium bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200 border border-gray-200 dark:border-slate-700 shadow-xs"
                          >
                            <Avatar user={member} size="xxs" />
                            <span className="truncate max-w-[90px] leading-none">{member.name || "User"}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleAssignee(member);
                              }}
                              className="hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0"
                            >
                              <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                            </button>
                          </span>
                        ))}
                        <input
                          ref={memberInputRef}
                          type="text"
                          value={memberSearch}
                          onChange={(e) => {
                            setMemberSearch(e.target.value);
                            if (!showMembersDropdown) setShowMembersDropdown(true);
                          }}
                          onFocus={() => setShowMembersDropdown(true)}
                          placeholder={assignees.length === 0 ? "Search assignees..." : ""}
                          className="bg-transparent outline-none flex-1 min-w-[60px] text-xs text-gray-900 dark:text-slate-100 placeholder:text-gray-400"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMembersDropdown((prev) => !prev);
                        }}
                        className="p-0.5 text-gray-500 hover:text-gray-700 dark:hover:text-slate-300 flex-shrink-0 self-center"
                      >
                        <ChevronDown
                          className={`w-4 h-4 transition-transform flex-shrink-0 ${
                            showMembersDropdown ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <CardMembersDropdown
                    isOpen={showMembersDropdown}
                    onClose={() => setShowMembersDropdown(false)}
                    anchorRef={assigneeSectionRef}
                    memberSearch={memberSearch}
                    projectMembers={projectMembers}
                    selectedAssignees={selectedAssigneeIds}
                    onToggleAssignee={handleToggleAssignee}
                  />
                </div>


                {/* File Attachments */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="w-4 h-4 text-gray-700 dark:text-slate-300" />
                    <label className="text-sm font-semibold text-gray-700 dark:text-slate-300">
                      Documents ({getOtherAttachments().length})
                    </label>
                  </div>
                  <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                    {getOtherAttachments().length > 0 ? (
                      getOtherAttachments().map((attachment) => (
                        <div
                          key={attachment._id || attachment.id}
                          className="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-slate-700/30 rounded-lg group hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors duration-200"
                        >
                          <span className="text-lg">
                            {getFileIcon(attachment)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white text-xs truncate">
                              {attachment.originalName ||
                                attachment.filename ||
                                attachment.name}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-slate-400">
                              {attachment.uploadedAt
                                ? new Date(
                                  attachment.uploadedAt
                                ).toLocaleString()
                                : "Unknown date"}
                            </p>
                          </div>
                          <div className="flex items-center space-x-1">
                            <a
                              href={
                                attachment?.url?.startsWith("http")
                                  ? attachment?.url
                                  : `${API_URL}${attachment?.url}`
                              }
                              onClick={(e) =>
                                handleViewAttachment(
                                  e,
                                  attachment?.url?.startsWith("http")
                                    ? attachment?.url
                                    : `${API_URL}${attachment?.url}`
                                )
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-[12px] font-semibold transition-colors duration-200 hover:underline flex items-center gap-1"
                            >
                              View
                            </a>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAttachmentToDelete({
                                  id: attachment._id || attachment.id,
                                  closeImageModal: false,
                                });
                                setShowDeleteAttachmentConfirm(true);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-all duration-200 text-xs"
                              title="Delete attachment"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-slate-500 italic text-xs">
                        No file attachments
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    {/* File Upload */}
                    <div className="space-y-1">
                      <div className="flex space-x-1">
                        <input
                          type="file"
                          multiple
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip,.rar,.7z,.json,.xml,.odt,.ods,.odp"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className="flex-1 h-8 bg-blue-600 text-white hover:bg-blue-700 font-medium px-2 rounded-lg transition-colors duration-200 text-xs cursor-pointer flex items-center justify-center space-x-1"
                        >
                          <Upload className="w-3 h-3" />
                          <span>Upload Files (Max 5)</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-500 text-start">
                        Max 5 files, 25MB each. Supports images and documents.
                      </p>
                    </div>

                    {/* URL Attachment */}
                    <div className="flex space-x-1 mt-2">
                      <input
                        type="url"
                        value={attachmentUrl}
                        onChange={(e) => setAttachmentUrl(e.target.value)}
                        placeholder="Enter attachment URL..."
                        className="flex-1 h-8 px-2.5 border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                      />
                      <button
                        onClick={handleAddAttachment}
                        disabled={!attachmentUrl.trim()}
                        className="h-8 bg-gray-600 text-white hover:bg-gray-700 font-medium px-3 rounded-lg transition-colors duration-200 text-xs flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Archive Card Button */}
                <div className="space-y-2 pb-3">
                  {card.isArchived ? (
                    <>
                      <button
                        onClick={handleRestore}
                        disabled={loading}
                        className="w-full h-8 bg-green-600 text-white hover:bg-green-700 font-medium px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-sm disabled:opacity-50"
                        title="Restore card"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Restore Card</span>
                      </button>
                      <button
                        onClick={handleDelete}
                        disabled={loading}
                        className="w-full h-8 bg-red-600 text-white hover:bg-red-700 font-medium px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-sm disabled:opacity-50"
                        title="Permanently delete card"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </>
                  ) : (
                    // Hide Archive button when card is complete
                    !card.isComplete && (
                      <button
                        onClick={handleArchive}
                        className="w-full h-8 bg-[#4b6584] text-white hover:bg-[#778ca3] font-medium px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 text-[12px]"
                        title="Archive card"
                      >
                        <Archive className="w-4 h-4" />
                        <span>Archive Card</span>
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Drag Overlay */}
        {isDragging && (
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 m-0 bg-blue-600 bg-opacity-90 z-50 flex items-center justify-center pointer-events-none rounded-2xl"
            style={{ width: "40%", height: "30%" }}
          >
            <div className="text-center text-white">
              <Upload className="w-12 h-12 mx-auto mb-3 animate-bounce" />
              <p className="text-lg font-bold">Drop files here to upload</p>
              <p className="text-sm mt-1 opacity-90">
                Supports images and documents
              </p>
            </div>
          </div>
        )}

        {/* Upload Overlay */}
        {isUploading && (
          <div
            className="absolute top-0 left-0 right-0 bottom-0 m-0 bg-black bg-opacity-70 z-50 flex items-center justify-center pointer-events-none rounded-2xl"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="text-center text-white">
              <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-lg font-semibold">Uploading files...</p>
              <p className="text-xs mt-1 opacity-80">Please wait</p>
            </div>
          </div>
        )}
      </div>



      {/* Image Modal */}
      <ImagePreviewModal
        images={previewImages}
        activeIndex={previewImageIndex}
        isOpen={showImageModal && previewImages.length > 0}
        onNavigate={showImageAt}
        onClose={() => setShowImageModal(false)}
        onRequestDelete={(image) => {
          if (!image) return;
          setAttachmentToDelete({
            id: image._id || image.id,
            closeImageModal: true,
          });
          setShowDeleteAttachmentConfirm(true);
        }}
      />

      {/* Archive Card Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmArchive}
        title="Archive Card"
        message={`Are you sure you want to archive "${card.title}"? The card will be moved to the Archive column and can be restored later.`}
        confirmText="Archive Card"
        cancelText="Cancel"
        type="warning"
        isLoading={loading}
      />

      {/* Delete Card Confirmation Modal */}
      {showDeleteCardConfirm && (
        <ConfirmationModal
          isOpen={showDeleteCardConfirm}
          onClose={(e) => {
            // Prevent event propagation to card modal
            if (e) {
              e.stopPropagation();
            }
            if (!loading) {
              setShowDeleteCardConfirm(false);
            }
          }}
          onConfirm={(e) => {
            // Prevent event propagation to card modal
            if (e) {
              e.stopPropagation();
            }
            confirmDelete();
          }}
          title="Permanently Delete Card"
          message={`Are you sure you want to permanently delete "${card.title}"? This action cannot be undone and will remove the card from the database.`}
          confirmText="Delete Permanently"
          cancelText="Cancel"
          type="danger"
          isLoading={loading}
        />
      )}

      {/* Unassign User Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnassignConfirm}
        onClose={(e) => {
          if (e) {
            e.stopPropagation();
          }
          if (!isUnassigning) {
            setShowUnassignConfirm(false);
            setUserToUnassign(null);
          }
        }}
        onConfirm={(e) => {
          if (e) {
            e.stopPropagation();
          }
          if (userToUnassign) {
            handleUnassignUser(userToUnassign._id || userToUnassign.id);
          }
        }}
        title="Remove member?"
        message={
          userToUnassign
            ? `Remove ${userToUnassign.name || "this user"} from "${card.title || "this card"}"? They will lose access to this card.`
            : "Remove this user from the card?"
        }
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
        isLoading={isUnassigning}
      />

      {/* Delete attachment confirmation modal */}
      <ConfirmationModal
        isOpen={showDeleteAttachmentConfirm}
        onClose={(e) => {
          if (e) {
            e.stopPropagation();
          }
          if (!deletingAttachment) {
            setShowDeleteAttachmentConfirm(false);
            setAttachmentToDelete(null);
          }
        }}
        onConfirm={(e) => {
          if (e) {
            e.stopPropagation();
          }
          confirmDeleteAttachment(e);
        }}
        title="Delete attachment?"
        message="Deleting an attachment is permanent. There is no undo."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={deletingAttachment}
      />

      {/* Delete comment confirmation modal */}
      <ConfirmationModal
        isOpen={showDeleteCommentConfirm}
        onClose={(e) => {
          if (e) e.stopPropagation();
          setShowDeleteCommentConfirm(false);
          setCommentToDelete(null);
        }}
        onConfirm={(e) => {
          if (e) e.stopPropagation();
          if (commentToDelete) {
            handleDeleteComment(commentToDelete._id || commentToDelete.id);
          }
        }}
        title="Delete comment?"
        message="Deleting this comment is permanent and will also delete any replies to it. There is no undo."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />


      {/* Formatting Help Modal */}
      {showFormattingHelp && (
        <div className="modal-overlay">
          <div
            ref={formattingHelpModalRef}
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Markdown Formatting Help
                </h3>
                <button
                  onClick={() => setShowFormattingHelp(false)}
                  className="p-1 rounded hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    **bold**
                  </code>
                  <span className="ml-2 text-gray-600">
                    → <strong>bold</strong>
                  </span>
                </div>
                <div>
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    *italic*
                  </code>
                  <span className="ml-2 text-gray-600">
                    → <em>italic</em>
                  </span>
                </div>
                <div>
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    ## Header
                  </code>
                  <span className="ml-2 text-gray-600">→ Header (larger)</span>
                </div>
                <div>
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    - List item
                  </code>
                  <span className="ml-2 text-gray-600">→ • List item</span>
                </div>
                <div>
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    [link](url)
                  </code>
                  <span className="ml-2 text-gray-600">
                    →{" "}
                    <a href="#" className="text-blue-600">
                      link
                    </a>
                  </span>
                </div>
                <div>
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                    ---
                  </code>
                  <span className="ml-2 text-gray-600">→ Horizontal rule</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-700">
                      🧹 Clear Formatting:
                    </span>
                    <span className="text-xs text-gray-600">
                      Removes all markdown from selected text
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowFormattingHelp(false)}
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CardModal;
